import React, { useEffect, useRef, useState } from 'react';
import { useFaceLandmarker } from '../hooks/useFaceLandmarker';
import { useObjectDetector } from '../hooks/useObjectDetector';
import { usePoseLandmarker } from '../hooks/usePoseLandmarker';
import { useHandLandmarker } from '../hooks/useHandLandmarker';
import { computeHomography, PAPER_DST, PAPER_CORNERS_DEFAULT, PAPER_ZONE_META, type Point2D } from '../engine/Homography';
import { handOverPaper, phoneNearPaper } from '../engine/PaperRelations';
import { ObjectTracker, type TrackedObject, type RawDetection } from '../engine/ObjectTracker';
import { detectPenYolo, getPenYoloStatus, type PenRoi } from '../engine/PenYoloDetector';
// Patent-gap observation pipeline (2026-07-08) — additive, ?obsVec=on only.
import { buildTargetRegistry } from '../engine/TargetRegistry';
import { gazeTargetLikelihood } from '../engine/GazeTargetLikelihood';
import { buildObservationVector, type ObservationVector } from '../engine/ObservationVector';
import { DurationHistogram } from '../engine/DurationHistogram';
import {
  interpretGazeEvent, candidateFromLikelihood,
  type ExamMode, type Interpretation,
} from '../engine/ConditionalInterpretation';
import type { PartialHomographyKind } from '../engine/Homography';
import type { PoseHeadEstimate } from '../hooks/usePoseLandmarker';
import type { PoseAuxInfo, PhoneGripAuxInfo, PaperZoneState, FaceBbox } from '../hooks/useFaceLandmarker';
import type { Detection } from '../hooks/useObjectDetector';
import { HeadTremorDetector, type TremorMetric as HeadTremorMetric } from '../engine/HeadTremorDetector';
import { DownwardPostureDetector, type DownwardPostureResult } from '../engine/DownwardPostureDetector';
import { PalmExposureDetector, type PalmExposureResult } from '../engine/PalmExposureDetector';
import { MultiPaperPrimarySelector, type PrimarySelection } from '../engine/MultiPaperPrimarySelector';
import { MultiChannelAccumulator, type LongWindowSnapshot } from '../engine/LongWindowAccumulator';
import { attachScreenWatcher, buildEvidence } from '../engine/EvidenceBuilder';
import type { BehaviorType, EvidenceContext, RiskLevel } from '../types';
import { useMultiFaceLandmarker } from '../hooks/useMultiFaceLandmarker';
import { JointAttentionDetector, type JointAttentionFrame, type Roi, type DyadState } from '../engine/JointAttentionDetector';
import { analyzeFraming, verdictColor, type FramingResult } from '../engine/CameraFovHelper';
import { classifyPartialVisibility, type PartialGazeEstimate } from '../engine/PartialVisibilityClassifier';
import { classifyGazeZone, ZONE_META, type GazeZone } from '../engine/GazeZoneClassifier';
import { ModalitySemanticDetector, type SemanticVerdict } from '../engine/ModalitySemanticDetector';
import { CoOccurrenceDetector } from '../engine/CoOccurrenceDetector';
import { classifyBodyLean, gazeAreaPhrase, handPhrase, bodyPhrase, verdictRisk } from '../engine/ModalityVerdict';
import {
  classifyArmFallback, intersectArmWithObjects,
  type ArmFallbackResult, type ArmObjectIntersection,
} from '../engine/ArmFallbackDetector';
import { detectPaperClip, type PaperClipResult } from '../engine/PaperClipDetector';
import { detectMultiPaper, type MultiPaperResult } from '../engine/MultiPaperDetector';
import { detectPaperRegion } from '../engine/PaperRegionDetector';
import { detectPenObject, type PenObjectResult } from '../engine/PenObjectDetector';
import { detectPhoneObject, type PhoneObjectResult } from '../engine/PhoneObjectDetector';
import {
  classifyHandObject, classifyHandObjectSync, preloadHandObjectClassifier,
  getModelStatus, type HandObjectResult, type ModelStatus,
} from '../engine/HandObjectClassifier';
import { generateMarkerDataURL } from '../engine/MarkerPatterns';

const RAW_VIDEO_ID = 'liveRawVideo';
const OVERLAY_ID = 'liveOverlay';
const HAND_OVERLAY_ID = 'liveHandOverlay';
const OBJECT_OVERLAY_ID = 'liveObjectOverlay';
const COMPOSITE_CANVAS_ID = 'liveCompositeCanvas';
const COMPOSED_VIDEO_ID_REAL = 'liveComposedVideo';

// ── paper detection mode (URL param `?paperMode=...`) ────────
// detect    — image processing on raw webcam (current)         [default]
// composite — synthetic markers composited into frame, marker detector
//             reads them; robust because markers always present
// fixed     — outline always shown at a fixed desk position; user
//             physically places paper at that spot
// marker    — fiducial markers printed on paper; marker detector runs
//             on raw webcam without composite
type PaperMode = 'detect' | 'composite' | 'fixed' | 'marker';
function getPaperMode(): PaperMode {
  if (typeof window === 'undefined') return 'composite';
  const m = new URLSearchParams(window.location.search).get('paperMode');
  if (m === 'composite' || m === 'fixed' || m === 'marker' || m === 'detect') return m;
  return 'composite';  // default — verified best on student-actor recordings
}
const PAPER_MODE: PaperMode = getPaperMode();

// ── held-object temporal onset gate (URL param `?heldGate=off`) ──────
// A NEW positive held-object class (phone/pen) must persist for
// HELD_ONSET_CONFIRM_MS before it is committed to the displayed state.
// Real held objects stay in the hand for many frames; EfficientDet /
// image-proc false positives flicker for 1-2 frames (CEO 0524 12:58: a
// pen briefly scored 0.41 'cell phone'). The previous logic committed the
// class instantly AND then held it for 1 s, turning a single spurious
// frame into a full-second false alert. The onset gate is the rising-edge
// counterpart to that 1 s dropout hold. Set `?heldGate=off` to revert to
// instant commit for A/B comparison.
const HELD_GATE_ENABLED: boolean =
  typeof window === 'undefined'
    ? true
    : new URLSearchParams(window.location.search).get('heldGate') !== 'off';
const HELD_ONSET_CONFIRM_MS = 300;
const HELD_DROPOUT_HOLD_MS = 1000;

// ── hidden-phone-downPosture score model (URL param `?postureModel=linear`) ────
// 'gated' (default) — head-gated top-2 corroboration: a downPosture needs
// looking down past normal reading angle AND ≥2 corroborating channels,
// so a lone wrist drop while reading no longer crosses threshold.
// 'linear' — legacy substitutive weighted sum, for A/B comparison.
const POSTURE_SCORE_MODEL: 'gated' | 'linear' =
  typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('postureModel') === 'linear'
    ? 'linear' : 'gated';

// ── per-modality semantic logging — runtime sign toggles ────────────
// Verdict helpers (classifyBodyLean, *Phrase, verdictRisk) live in
// engine/ModalityVerdict so the scene-log app and live demo share one
// source for wording + the mirror-independent lean sign.
// `?bodyLeanFlip=1` swaps the body left/right label at runtime.
const BODY_LEAN_FLIP: boolean =
  typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('bodyLeanFlip') === '1';
// Gaze-area zone uses pose.head.yaw, whose "+ = right" is in RAW
// (un-mirrored) image coordinates — subject's LEFT is on the image RIGHT,
// so we negate yaw to read zones in the subject's frame. `?gazeYawFlip=1`
// reverts to the raw sign without a redeploy.
const GAZE_YAW_FLIP: boolean =
  typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('gazeYawFlip') === '1';
// Pen YOLO object-detector (integration scaffold) — OFF until pen_detect.onnx
// is trained + placed in public/. Enable with `?penOd=on`. When on, its
// detections are merged into the ObjectTracker input alongside EfficientDet.
const PEN_OD_ENABLED: boolean =
  typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('penOd') === 'on';
// Observation-pipeline tap (0704 stage 1→2→3): assembles the unified target
// registry + observation vector x(t) from existing detectors. OFF by default;
// enable with `?obsVec=on`. Passive — feeds no verdict.
const OBS_VEC_ENABLED: boolean =
  typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('obsVec') === 'on';
// Task context for the conditional-decode stage — `?examMode=open_book|
// closed_book|homework|practice` (default 'unspecified'). Drives whether a
// reference-material glance is suppressed as normal or surfaced for review.
const EXAM_MODE: ExamMode = (() => {
  const v = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('examMode') : null;
  return (v === 'open_book' || v === 'closed_book' || v === 'homework' || v === 'practice')
    ? v : 'unspecified';
})();

// Hand landmarks → normalized bbox per hand (for pen-detector hand-ROI crop).
// Accepts MediaPipe hand landmarks (array of hands; each hand = array of {x,y}).
function penHandRois(handLms: any[] | null | undefined): PenRoi[] {
  if (!handLms || !handLms.length) return [];
  const out: PenRoi[] = [];
  for (const h of handLms) {
    if (!h || !h.length) continue;
    let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
    for (const p of h) {
      const x = p?.x ?? p?.[0], y = p?.y ?? p?.[1];
      if (typeof x !== 'number' || typeof y !== 'number') continue;
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
    }
    if (x1 > x0 && y1 > y0) out.push({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
  }
  return out;
}

// COMPOSED_VIDEO_ID switches based on mode — composite needs a separate
// video element fed by a captureStream from the composite canvas.
const COMPOSED_VIDEO_ID = PAPER_MODE === 'composite' ? COMPOSED_VIDEO_ID_REAL : RAW_VIDEO_ID;

const MARKER_SIZE_NORM = 0.07;

/**
 * LiveDemoApp — DrSimon 멀티모달 프록터링 데모
 *
 * ⚠️  시선 판정 채널 분기 메모 (2026-06 Task #4)
 * ─────────────────────────────────────────────
 * 이 컴포넌트의 시선 신호는 **MediaPipe FaceLandmarker 헤드포즈(yaw / pitch)**입니다.
 * 동공 기반 BlazeGaze 아이트래커(useWebEyeTracker)는 사용하지 않습니다.
 *
 * 반면 App.tsx(메인 프로덕션 경로)는:
 *   const eyeTracker = useWebEyeTracker(VIDEO_ELEMENT_ID);          // BlazeGaze 동공 트래커
 *   const activeGaze = eyeTracker.gazeData ?? face.gazeData;        // iris fallback
 * 구조로 BlazeGaze → FaceLandmarker iris 2단 폴백을 사용합니다.
 *
 * CEO 보고서 검증 수치(FPR·recall)는 모두 이 컴포넌트(헤드포즈)로 산출됩니다.
 * 동공 아이트래킹 채널 추가 시 탐지율 추가 향상 가능 — 현재 수치는 보수적 하한.
 */
export function LiveDemoApp() {
  const detectionsRef = useRef<Detection[]>([]);
  const paperHomographyRef = useRef<Float64Array | null>(null);
  const paperCornersRef = useRef<Point2D[]>([...PAPER_CORNERS_DEFAULT]);
  const calibHomographyRef = useRef<Float64Array | null>(null);
  const calibCornersRef = useRef<Point2D[]>([...PAPER_CORNERS_DEFAULT]);
  const poseHeadRef = useRef<PoseHeadEstimate>({
    yaw: 0, pitch: 0, noseX: 0.5, noseY: 0.3, valid: false,
  });
  const poseAuxRef = useRef<PoseAuxInfo | null>(null);
  const phoneGripRef = useRef<PhoneGripAuxInfo | null>(null);
  const paperZoneStateRef = useRef<PaperZoneState | null>(null);
  const faceBboxRef = useRef<FaceBbox | null>(null);

  const [margin, setMargin] = useState(0.15);
  const marginRef = useRef(margin);
  useEffect(() => { marginRef.current = margin; }, [margin]);

  const dwellRef = useRef({ inside: 0, boundary: 0, outside: 0, total: 0 });
  const lastSampleRef = useRef(0);
  const paperDetectCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [paperDetectInfo, setPaperDetectInfo] = useState<{ found: boolean; conf: number }>({
    found: false, conf: 0,
  });
  const penDetectCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [penObject, setPenObject] = useState<PenObjectResult>({
    present: false, confidence: 0, handBbox: { x: 0, y: 0, w: 0, h: 0 },
  });
  const phoneDetectCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [phoneObject, setPhoneObject] = useState<PhoneObjectResult>({
    present: false, confidence: 0, handBbox: { x: 0, y: 0, w: 0, h: 0 },
  });
  // MobileNetV3-Small pen/phone classifier (ONNX). Async inference is
  // single-flight — UI uses the latest result, refresh ~5 Hz.
  const handObjCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [handObj, setHandObj] = useState<HandObjectResult>({
    dominant: 'none', confidence: 0,
    noneProb: 0, penProb: 0, phoneProb: 0,
    handBbox: { x: 0, y: 0, w: 0, h: 0 },
  });
  // Track ML model status (loading/ready/failed) for HUD indicator.
  const [mlStatus, setMlStatus] = useState<{ status: ModelStatus; error: string | null }>(
    { status: 'loading', error: null });
  useEffect(() => {
    const id = window.setInterval(() => {
      const s = getModelStatus();
      setMlStatus(s);
      // Status is static once terminal — stop polling (no forever 2 Hz timer).
      if (s.status === 'ready' || s.status === 'failed') window.clearInterval(id);
    }, 500);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const H = computeHomography(
      PAPER_CORNERS_DEFAULT as [Point2D, Point2D, Point2D, Point2D], PAPER_DST,
    );
    if (H) {
      paperHomographyRef.current = H;
      calibHomographyRef.current = new Float64Array(H);
    }
  }, []);

  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState<string | null>(null);
  // Pre-flight / health: poll the raw video element for frame flow + resolution.
  const [videoHealth, setVideoHealth] = useState<{ w: number; h: number; ready: number }>(
    { w: 0, h: 0, ready: 0 });
  useEffect(() => {
    const id = window.setInterval(() => {
      const v = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement | null;
      setVideoHealth({ w: v?.videoWidth ?? 0, h: v?.videoHeight ?? 0, ready: v?.readyState ?? 0 });
    }, 500);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let disposed = false;
    let stream: MediaStream | null = null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: false,
        });
        if (disposed) { stream.getTracks().forEach(t => t.stop()); return; }
        const rawVid = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement;
        if (rawVid) {
          rawVid.srcObject = stream;
          rawVid.onloadedmetadata = () => { rawVid.play().catch(() => {}); };
          rawVid.play().catch(() => {});
        }
        setCamReady(true);
      } catch (e: unknown) {
        setCamError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      disposed = true;
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ── Paper detection — mode-switched ─────────────────────────────────
  // 'fixed' / 'composite' / 'marker' modes use the static PAPER_CORNERS_DEFAULT
  // (composite stamps markers there, marker reads real fiducials there,
  // fixed just always draws the outline). Only 'detect' runs image processing.
  useEffect(() => {
    if (!camReady) return;
    let stopped = false;
    const rawVid = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement | null;
    if (!rawVid) return;

    if (PAPER_MODE === 'fixed' || PAPER_MODE === 'composite' || PAPER_MODE === 'marker') {
      // Pin paper at the fixed default position. Homography from those
      // corners to PAPER_DST is solved once. No detection loop needed.
      const H = computeHomography(
        PAPER_CORNERS_DEFAULT as [Point2D, Point2D, Point2D, Point2D], PAPER_DST,
      );
      if (H) {
        paperHomographyRef.current = H;
        calibHomographyRef.current = new Float64Array(H);
      }
      paperCornersRef.current = [...PAPER_CORNERS_DEFAULT];
      calibCornersRef.current = [...PAPER_CORNERS_DEFAULT];
      setPaperDetectInfo({ found: true, conf: 1 });
      return;  // no polling
    }

    if (!paperDetectCanvasRef.current) {
      paperDetectCanvasRef.current = document.createElement('canvas');
    }

    // After this many consecutive failed detections, hide the stale paper
    // outline. ~5 seconds at 4 Hz — paper detector is unreliable while
    // hands move over the sheet, so we tolerate longer dropouts before
    // clearing the outline.
    const STALE_LIMIT = 20;
    let staleStreak = 0;
    // EMA-smoothed corners — paper detection on a single noisy frame can
    // jump as the hand moves over the sheet. Heavier smoothing (lower α)
    // makes the outline track the paper's true position rather than each
    // momentarily-occluded extremum.
    const EMA_ALPHA = 0.20;
    let smoothedCorners: Point2D[] | null = null;

    const tick = () => {
      if (stopped) return;
      const result = detectPaperRegion(rawVid, paperDetectCanvasRef.current!);
      // Lowered accept threshold — accept reasonably likely paper even when
      // hand occlusion deforms the segmentation. EMA smoothing handles the
      // resulting noise.
      if (result && result.confidence > 0.28) {
        staleStreak = 0;
        const raw = result.corners;
        if (!smoothedCorners) {
          smoothedCorners = raw.map((c) => ({ x: c.x, y: c.y }));
        } else {
          for (let i = 0; i < 4; i++) {
            smoothedCorners[i] = {
              x: smoothedCorners[i].x * (1 - EMA_ALPHA) + raw[i].x * EMA_ALPHA,
              y: smoothedCorners[i].y * (1 - EMA_ALPHA) + raw[i].y * EMA_ALPHA,
            };
          }
        }
        paperCornersRef.current = smoothedCorners.map((c) => ({ x: c.x, y: c.y }));
        calibCornersRef.current = smoothedCorners.map((c) => ({ x: c.x, y: c.y }));
        const H = computeHomography(
          paperCornersRef.current as [Point2D, Point2D, Point2D, Point2D],
          PAPER_DST,
        );
        if (H) {
          paperHomographyRef.current = H;
          calibHomographyRef.current = new Float64Array(H);
        }
        setPaperDetectInfo({ found: true, conf: result.confidence });
      } else {
        staleStreak++;
        if (staleStreak >= STALE_LIMIT) {
          // Clear homography so useFaceLandmarker stops drawing the outline.
          paperHomographyRef.current = null;
          calibHomographyRef.current = null;
          smoothedCorners = null;
        }
        setPaperDetectInfo({ found: false, conf: result?.confidence ?? 0 });
      }
    };

    const id = window.setInterval(tick, 250);
    tick();
    return () => { stopped = true; window.clearInterval(id); };
  }, [camReady]);

  // ── Composite pipeline (only active in PAPER_MODE === 'composite') ───
  // Renders raw webcam + 4 fiducial markers at PAPER_CORNERS_DEFAULT to a
  // hidden canvas, then publishes that canvas via captureStream → hidden
  // composed video element. MediaPipe hooks read COMPOSED_VIDEO_ID and so
  // see the markers in the frame, allowing the marker detector to lock on.
  useEffect(() => {
    if (!camReady) return;
    if (PAPER_MODE !== 'composite') return;

    let cancelled = false;
    let composedStream: MediaStream | null = null;
    let raf = 0;

    const markerImgs: HTMLImageElement[] = [0, 1, 2, 3].map((id) => {
      const img = new Image();
      img.src = generateMarkerDataURL(id, 120);
      return img;
    });

    const rawVid = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement | null;
    const canvas = document.getElementById(COMPOSITE_CANVAS_ID) as HTMLCanvasElement | null;
    const composedVid = document.getElementById(COMPOSED_VIDEO_ID_REAL) as HTMLVideoElement | null;
    if (!rawVid || !canvas || !composedVid) return;

    const tick = () => {
      if (cancelled) return;
      raf = requestAnimationFrame(tick);
      const vw = rawVid.videoWidth;
      const vh = rawVid.videoHeight;
      if (!vw || !vh) return;
      if (canvas.width !== vw || canvas.height !== vh) {
        canvas.width = vw; canvas.height = vh;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(rawVid, 0, 0, vw, vh);
      const sizePx = Math.max(40, Math.round(vh * MARKER_SIZE_NORM));
      for (let i = 0; i < 4; i++) {
        const cx = PAPER_CORNERS_DEFAULT[i].x * vw;
        const cy = PAPER_CORNERS_DEFAULT[i].y * vh;
        const img = markerImgs[i];
        if (img.complete) {
          ctx.drawImage(img, cx - sizePx / 2, cy - sizePx / 2, sizePx, sizePx);
        }
      }
      if (!composedStream && (canvas as HTMLCanvasElement & {
        captureStream?: (fr: number) => MediaStream
      }).captureStream) {
        composedStream = (canvas as HTMLCanvasElement & {
          captureStream: (fr: number) => MediaStream
        }).captureStream(30);
        composedVid.srcObject = composedStream;
        composedVid.play().catch(() => {});
      }
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (composedStream) composedStream.getTracks().forEach((t) => t.stop());
    };
  }, [camReady]);

  const face = useFaceLandmarker(
    COMPOSED_VIDEO_ID, OVERLAY_ID, detectionsRef,
    paperHomographyRef, paperCornersRef,
    poseHeadRef, calibHomographyRef, calibCornersRef,
    poseAuxRef, phoneGripRef, paperZoneStateRef, marginRef,
    faceBboxRef,
  );

  const headTremorRef = useRef(new HeadTremorDetector());
  const [headTremor, setHeadTremor] = useState<HeadTremorMetric>({
    amplitudePx: 0, freqHz: 0, count: 0, confidence: 0,
  });
  useEffect(() => {
    const id = setInterval(() => {
      const v = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement | null;
      const side = v?.videoWidth || 1280;
      const lms = face.landmarksRef.current;
      const m = headTremorRef.current.update(lms ?? null, performance.now(), side);
      setHeadTremor(m);
    }, 100);
    return () => clearInterval(id);
  }, [face.landmarksRef]);

  // ── Downward posture detector ────────────────────────────────────
  // Multi-signal fusion (head pitch + torso twist/tilt + wrist below
  // frame + gaze below paper) sustained ≥ 1.5 s.
  const downPostureDetectorRef = useRef(new DownwardPostureDetector(POSTURE_SCORE_MODEL));
  const [downPosture, setDownPosture] = useState<DownwardPostureResult>({
    active: false, score: 0, sustainedSec: 0, eventCountWindow: 0, escalation: 'none',
    signals: { pitch: 0, twist: 0, shTilt: 0, wrist: 0, gaze: 0 },
  });
  // Session start time for cumulative stats.
  const sessionStartRef = useRef<number>(Date.now());
  // Down-posture event log — push timestamp when it transitions to active.
  // Cleared by reload; useful as live "evidence" for the demo audience.
  const [downPostureEvents, setDownPostureEvents] = useState<Array<{ ts: number; dur: number }>>([]);
  const wasActiveRef = useRef(false);
  const activeSinceRef = useRef<number | null>(null);
  useEffect(() => {
    if (downPosture.active && !wasActiveRef.current) {
      activeSinceRef.current = Date.now();
      recordEvidence('posture', 'high', '고개 숙임·손 비노출 자세 패턴 지속 시작', face.hasFace);
    } else if (!downPosture.active && wasActiveRef.current && activeSinceRef.current) {
      const ts = activeSinceRef.current;
      const dur = (Date.now() - ts) / 1000;
      setDownPostureEvents(es => [...es.slice(-9), { ts, dur }]);
      activeSinceRef.current = null;
      recordEvidence('posture', 'med', `자세 패턴 종료 (${dur.toFixed(1)}s 지속)`, face.hasFace);
    }
    wasActiveRef.current = downPosture.active;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downPosture.active]);
  // Latest pose snapshot for the downPosture tick (rAF-paced via refs so the
  // tick doesn't depend on hook re-renders).
  const downPostureInputRef = useRef<{
    pitch: number;
    landmarks: typeof pose.rawLandmarks;
    paperBottomY: number;
    gazeEndpointY: number;
  }>({
    pitch: 0, landmarks: null, paperBottomY: 0, gazeEndpointY: 0,
  });
  useEffect(() => {
    const id = window.setInterval(() => {
      const inp = downPostureInputRef.current;
      const r = downPostureDetectorRef.current.update({
        poseLandmarks: inp.landmarks,
        headPitchDeg: inp.pitch,
        gazeEndpointY: inp.gazeEndpointY,
        paperBottomY: inp.paperBottomY,
        now: performance.now(),
      });
      setDownPosture(r);
    }, 150);
    return () => window.clearInterval(id);
  }, []);

  const multiFace = useMultiFaceLandmarker(COMPOSED_VIDEO_ID, true);
  const jaDetectorRef = useRef(new JointAttentionDetector());
  const [jaFrame, setJaFrame] = useState<JointAttentionFrame>({
    assignments: [], mutualGazes: [], jointAttentions: [], faceCount: 0,
    metrics: {
      dyadObservedMs: 0, jointAttentionMs: 0, mutualGazeMs: 0, disengagedMs: 0,
      jai: 0, mgi: 0, disengagementRate: 0,
      initiations: {}, initiationAsymmetry: Number.NaN,
      meanSyncLagMs: Number.NaN, syncLagSamples: 0,
    },
    dyadState: 'absent',
  });
  // Phase 2-C — ring buffer of (ts, dyadState) samples for the Gantt
  // timeline. Window is 60 s; we expect ~10 Hz from setJaFrame so cap
  // length at 700 to be safe.
  const jaTimelineRef = useRef<Array<{ ts: number; state: DyadState }>>([]);
  const paperRoi: Roi = {
    id: 'paper',
    bbox: {
      x: Math.min(PAPER_CORNERS_DEFAULT[0].x, PAPER_CORNERS_DEFAULT[3].x),
      y: Math.min(PAPER_CORNERS_DEFAULT[0].y, PAPER_CORNERS_DEFAULT[1].y),
      w: Math.max(PAPER_CORNERS_DEFAULT[1].x, PAPER_CORNERS_DEFAULT[2].x)
         - Math.min(PAPER_CORNERS_DEFAULT[0].x, PAPER_CORNERS_DEFAULT[3].x),
      h: Math.max(PAPER_CORNERS_DEFAULT[2].y, PAPER_CORNERS_DEFAULT[3].y)
         - Math.min(PAPER_CORNERS_DEFAULT[0].y, PAPER_CORNERS_DEFAULT[1].y),
    },
  };
  // Phase 2-E — user-drawn ROIs (in image-normalized coords). Persisted
  // to localStorage so the demo survives page reloads.
  const [customRois, setCustomRois] = useState<Roi[]>(() => {
    try {
      const raw = localStorage.getItem('liveDemo.customRois');
      if (raw) return JSON.parse(raw) as Roi[];
    } catch { /* ignore */ }
    return [];
  });
  useEffect(() => {
    try { localStorage.setItem('liveDemo.customRois', JSON.stringify(customRois)); }
    catch { /* ignore */ }
  }, [customRois]);
  const [roiDrawMode, setRoiDrawMode] = useState(false);

  // Phase 3-C — primary selector state. tick is registered AFTER
  // multiPaper is declared (see below — `useEffect` reads `multiPaper`).
  const primarySelRef = useRef(new MultiPaperPrimarySelector(5000));
  const [primarySel, setPrimarySel] = useState<PrimarySelection>({
    primaryId: null, hitsById: {}, confidence: 0, totalHits: 0,
  });

  // ── EvidenceContext logging (Slide 20 횡단 원칙: timestamp + behavior
  //    + system + screen 4종 증거 동시 기록). attachScreenWatcher mounts
  //    once and starts tracking document visibilityState + window focus.
  //    Detector transition handlers call recordEvidence() — emits a
  //    LoggedEvent with EvidenceContext + risk + human-readable label.
  // Pipeline stage of a log line — lets the demo show the same modality's
  // raw signal, its windowed aggregation, and its final semantic verdict
  // as distinct timestamped entries (head-pose "왼쪽 반복 응시" template),
  // plus cross-modality fusion. 'verdict' is the default for the existing
  // transition-style logs.
  type LogStage = 'raw' | 'window' | 'verdict' | 'fusion';
  interface LoggedEvent {
    t: number;                   // wall-clock ms
    perfT: number;               // performance.now() ms (correlation)
    label: string;
    risk: RiskLevel;
    evidence: EvidenceContext;
    stage: LogStage;
    modality: string;            // e.g. 'gaze_area', 'hand', 'body', 'fusion'
  }
  const monitoringStartRef = useRef<number>(performance.now());
  const [evidenceLog, setEvidenceLog] = useState<LoggedEvent[]>([]);
  const evidenceLogRef = useRef<LoggedEvent[]>([]);
  evidenceLogRef.current = evidenceLog;
  useEffect(() => {
    const detach = attachScreenWatcher();
    return () => detach();
  }, []);
  const recordEvidence = (
    behaviorType: BehaviorType,
    risk: RiskLevel,
    label: string,
    faceVisible: boolean,
    stage: LogStage = 'verdict',
    modality: string = behaviorType,
  ) => {
    const now = performance.now();
    const ev = buildEvidence(behaviorType, {
      phase: 'monitoring',
      recordingMode: false,
      monitoringStartT: monitoringStartRef.current,
      faceVisible,
      now,
    });
    setEvidenceLog(es => [
      ...es.slice(-99),  // cap at last 100
      { t: Date.now(), perfT: now, label, risk, evidence: ev, stage, modality },
    ]);
  };

  // Phase 3-D — 30 s long-window accumulator (Slide 20 횡단 원칙).
  // Collects boolean signals from all detectors so the demo can show
  // patterns aggregated over a meaningful proctor-grade window instead
  // of momentary 1-2 s flapping.
  const longWindowRef = useRef(new MultiChannelAccumulator(30_000));
  const [longWindowSnap, setLongWindowSnap] = useState<Record<string, LongWindowSnapshot>>({});

  // Phase 3-B — Palm exposure state.
  const palmDetectorRef = useRef(new PalmExposureDetector());
  const [palm, setPalm] = useState<PalmExposureResult>({
    area: 0, smoothArea: 0, exposed: false, hiddenSustained: false, hiddenSec: 0,
  });
  const palmWasHiddenRef = useRef(false);
  useEffect(() => {
    if (palm.hiddenSustained && !palmWasHiddenRef.current) {
      recordEvidence('hand', 'med', `palm hidden ≥1.5s (${palm.hiddenSec.toFixed(1)}s)`, face.hasFace);
    } else if (!palm.hiddenSustained && palmWasHiddenRef.current) {
      recordEvidence('hand', 'low', 'palm visible again', face.hasFace);
    }
    palmWasHiddenRef.current = palm.hiddenSustained;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palm.hiddenSustained]);
  useEffect(() => {
    const id = window.setInterval(() => {
      const inp = phase3InputsRef.current;
      const now = performance.now();
      const r = palmDetectorRef.current.update(inp.handLandmarks, now);
      setPalm(r);
      const acc = longWindowRef.current;
      acc.pushBool('down_posture_active', inp.downPostureActive, now);
      acc.pushBool('down_posture_frequent', inp.downPostureFrequent, now);
      acc.pushBool('head_unknown', inp.headUnknownSec >= 1, now);
      acc.pushBool('palm_hidden', r.hiddenSustained, now);
      acc.pushBool('mutual_gaze', inp.mgActive, now);
      acc.pushBool('joint_attn', inp.jaActive, now);
      acc.pushBool('face_lost', !inp.faceHasFace, now);
      setLongWindowSnap(acc.snapshot(now));
      // Detection + tracking memory layer: stabilize object identity/class
      // across frames and coast through hand occlusion (Kay 0606).
      const baseDets = detectionsRef.current.map(d => ({
        label: d.label, labelKo: d.labelKo, score: d.score, bboxNorm: d.bboxNorm,
      }));
      // Merge pen YOLO detections (scaffold; empty unless ?penOd=on + model present)
      const penDets = PEN_OD_ENABLED ? penDetectionsRef.current : [];
      const tracked = objectTrackerRef.current.update(
        [...baseDets, ...penDets],
        now,
        { landmarks: inp.handLandmarks },
      );
      trackedObjectsDrawRef.current = tracked;
      setTrackedObjects(tracked);

      // ── Core-info relations → evidence log + 30s window (Kay 0606) ──
      // Surface hand-over-paper / phone-near-paper / tracked-object
      // interaction in the recorded evidence (so they reach reports/export),
      // not just the live HUD. Transition-edge only; neutral observation.
      const corners = paperCornersRef.current;
      const hOver = handOverPaper(inp.handLandmarks, corners).over;
      const phoneT = tracked.find(t => t.label === 'cell phone' && t.score >= 0.50);
      const phoneRel = phoneNearPaper(phoneT?.bbox ?? null, corners);
      const pNear = phoneRel.near;
      acc.pushBool('hand_over_paper', hOver, now);
      acc.pushBool('phone_near_paper', pNear, now);
      const fv = inp.faceHasFace;
      const prev = relPrevRef.current;
      if (hOver && !prev.handOver) {
        recordEvidence('hand', 'low', '손–시험지 가림 시작 (관찰)', fv, 'verdict', 'paper_rel');
      } else if (!hOver && prev.handOver) {
        recordEvidence('hand', 'low', '손–시험지 가림 해제', fv, 'verdict', 'paper_rel');
      }
      if (pNear && !prev.phoneNear) {
        recordEvidence('object', phoneRel.overlap ? 'med' : 'low',
          phoneRel.overlap ? '폰–시험지 겹침 시작 (관찰)' : '폰–시험지 근접 시작 (관찰)',
          fv, 'verdict', 'paper_rel');
      } else if (!pNear && prev.phoneNear) {
        recordEvidence('object', 'low', '폰–시험지 근접 해제', fv, 'verdict', 'paper_rel');
      }
      prev.handOver = hOver;
      prev.phoneNear = pNear;
      // Tracked-object interaction transitions (held / occluded-by-hand).
      const im = prev.interactions;
      for (const t of tracked) {
        const was = im[t.id] ?? 'none';
        if (t.interaction !== was
            && (t.interaction === 'held' || t.interaction === 'occluded_by_hand')) {
          const itxt = t.interaction === 'held' ? '파지 시작' : '손에 가림 시작';
          recordEvidence('object', 'low',
            `추적 객체 #${t.id} ${t.labelKo} ${itxt} (관찰)`, fv, 'verdict', 'track');
        }
        im[t.id] = t.interaction;
      }
      const liveIds = new Set(tracked.map(t => t.id));
      for (const k of Object.keys(im)) {
        if (!liveIds.has(Number(k))) delete im[Number(k)];
      }
    }, 150);
    return () => window.clearInterval(id);
  }, []);
  // Refs the late-bound tick reads from to avoid forward-decl ordering.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const phase3InputsRef = useRef<{
    handLandmarks: any[] | null;
    downPostureActive: boolean; downPostureFrequent: boolean;
    headUnknownSec: number;
    mgActive: boolean; jaActive: boolean;
    faceHasFace: boolean;
  }>({
    handLandmarks: null, downPostureActive: false, downPostureFrequent: false,
    headUnknownSec: 0, mgActive: false, jaActive: false, faceHasFace: false,
  });

  // Detection+tracking memory layer (Kay 0606): persistent object identity
  // + class vote + occlusion coasting + hand–object interaction state.
  const objectTrackerRef = useRef(new ObjectTracker());
  const [trackedObjects, setTrackedObjects] = useState<TrackedObject[]>([]);
  // Pen YOLO detections (scaffold) — merged into tracker input when ?penOd=on.
  const penDetectionsRef = useRef<RawDetection[]>([]);
  const penYoloCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [penYoloStatus, setPenYoloStatus] = useState<string>('idle');
  useEffect(() => {
    if (!PEN_OD_ENABLED) return;   // no model fetch / no-op until enabled
    if (!penYoloCanvasRef.current) penYoloCanvasRef.current = document.createElement('canvas');
    const id = window.setInterval(() => {
      const v = document.getElementById(COMPOSED_VIDEO_ID) as HTMLVideoElement | null;
      if (!v || !penYoloCanvasRef.current) return;
      // Hand-ROI crop: focus inference around the hand(s) (full-frame fallback
      // inside detectPenYolo when no hand). Temporal smoothing applied internally.
      const rois = penHandRois(phase3InputsRef.current?.handLandmarks);
      detectPenYolo(v, penYoloCanvasRef.current, rois)
        .then(r => { penDetectionsRef.current = r; })
        .catch(() => { /* model missing → stays [] */ });
      setPenYoloStatus(getPenYoloStatus().status);  // ready once .onnx loads
    }, 250);
    return () => window.clearInterval(id);
  }, []);
  // Draw/render-read mirror of the tracker output (rAF draw loop + render
  // read this instead of raw per-frame detections — v2 main-path routing).
  const trackedObjectsDrawRef = useRef<TrackedObject[]>([]);
  // Rising/falling-edge state for logging the paper relations + tracked
  // object interactions to the evidence log (report-visible, not just HUD).
  const relPrevRef = useRef<{
    handOver: boolean; phoneNear: boolean; interactions: Record<number, string>;
  }>({ handOver: false, phoneNear: false, interactions: {} });

  // ── Per-modality semantic pipeline (gaze-area / hand / body) + fusion.
  // Each modality maps its per-tick categorical state through a
  // ModalitySemanticDetector to a final verdict ("왼쪽 영역 반복 응시" …),
  // logging raw → window → verdict stages with timestamps; the
  // CoOccurrenceDetector then logs simultaneous cross-modality episodes.
  const gazeAreaDetRef = useRef(new ModalitySemanticDetector({ baseline: ['forward', 'own_paper'] }));
  const handDetRef = useRef(new ModalitySemanticDetector({ baseline: ['none', 'pen'] }));
  const bodyDetRef = useRef(new ModalitySemanticDetector({ baseline: ['upright'] }));
  const coOccRef = useRef(new CoOccurrenceDetector(1500, 2));
  const modalityInputRef = useRef<{
    yaw: number; pitch: number; shoulderAngle: number;
    held: string; faceVisible: boolean;
  }>({ yaw: 0, pitch: 0, shoulderAngle: 0, held: 'none', faceVisible: false });
  const rawLastRef = useRef<Record<string, { state: string; t: number }>>({});
  const verdictActiveRef = useRef<Record<string, boolean>>({});
  const [modalityVerdicts, setModalityVerdicts] =
    useState<Record<string, SemanticVerdict>>({});
  const [coOcc, setCoOcc] = useState<{ active: boolean; labels: string[] }>({
    active: false, labels: [],
  });
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = performance.now();
      const inp = modalityInputRef.current;
      const fv = inp.faceVisible;
      const signedYaw = GAZE_YAW_FLIP ? inp.yaw : -inp.yaw;  // subject-frame
      const gazeState: GazeZone = classifyGazeZone(signedYaw, inp.pitch);
      const handState = inp.held;  // 'none' | 'pen' | 'phone'
      const bodyState = classifyBodyLean(inp.shoulderAngle, BODY_LEAN_FLIP);
      const mods: Array<{
        key: string; behavior: BehaviorType; state: string;
        det: ModalitySemanticDetector; phrase: (v: SemanticVerdict) => string;
        raw: string;
      }> = [
        { key: 'gaze_area', behavior: 'gaze', state: gazeState, det: gazeAreaDetRef.current,
          phrase: gazeAreaPhrase,
          raw: `${ZONE_META[gazeState].label} (yaw ${signedYaw.toFixed(0)}° pitch ${inp.pitch.toFixed(0)}°)` },
        { key: 'hand', behavior: 'hand', state: handState, det: handDetRef.current,
          phrase: handPhrase, raw: `held=${handState}` },
        { key: 'body', behavior: 'posture', state: bodyState, det: bodyDetRef.current,
          phrase: bodyPhrase, raw: `어깨 ${inp.shoulderAngle.toFixed(1)}° → ${bodyState}` },
      ];
      const verdicts: Record<string, SemanticVerdict> = {};
      const signals = mods.map(m => {
        const v = m.det.update(m.state, now);
        verdicts[m.key] = v;
        // Stage 1 — raw signal, logged on state change (throttled ≥ 600 ms).
        const rl = rawLastRef.current[m.key];
        if (!rl || (rl.state !== m.state && now - rl.t >= 600)) {
          rawLastRef.current[m.key] = { state: m.state, t: now };
          recordEvidence(m.behavior, 'low', `[raw] ${m.raw}`, fv, 'raw', m.key);
        }
        // Stage 2 — window: a new qualified re-entry was counted.
        if (v.entryAdded) {
          recordEvidence(m.behavior, 'low',
            `[누적] ${m.key} 진입 ${v.entryCount}회 / window`, fv, 'window', m.key);
        }
        // Stage 3 — verdict: rising edge of the semantic conclusion.
        const wasActive = verdictActiveRef.current[m.key] ?? false;
        if (v.active && !wasActive) {
          recordEvidence(m.behavior, verdictRisk(v), m.phrase(v), fv, 'verdict', m.key);
        }
        verdictActiveRef.current[m.key] = v.active;
        return { modality: m.key, label: v.active ? m.phrase(v) : '', active: v.active };
      });
      setModalityVerdicts(verdicts);

      // Stage 4 — fusion: simultaneous verdicts across ≥ 2 modalities.
      const co = coOccRef.current.update(signals, now);
      setCoOcc({ active: co.active, labels: co.labels.filter(Boolean) });
      if (co.justActivated) {
        recordEvidence('scene', 'high',
          `동시 발생: ${co.labels.filter(Boolean).join(' + ')}`, fv, 'fusion', 'fusion');
      }
    }, 150);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Patent-gap observation pipeline tap (2026-07-08, ?obsVec=on) ─────
  // Passive, OFF by default. Once/second it assembles the unified
  // TargetRegistry → gaze-likelihood → ObservationVector x(t) from the
  // EXISTING detector outputs (tracked objects, paper zone/homography, head
  // yaw/pitch), and accumulates per-state dwell durations (HSMM prep). It
  // mutates no existing state and feeds no verdict — this is the live proof
  // that the 0704 stage 1→2→3 input path runs on real frames.
  const durationHistRef = useRef(new DurationHistogram());
  const obsDwellPrevRef = useRef<{ state: string; t: number } | null>(null);
  const [obsVec, setObsVec] = useState<ObservationVector | null>(null);
  const [obsInterp, setObsInterp] = useState<Interpretation | null>(null);
  useEffect(() => {
    if (!OBS_VEC_ENABLED) return;
    const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
    const id = window.setInterval(() => {
      const now = performance.now();
      const inp = modalityInputRef.current;
      const objs = trackedObjectsDrawRef.current ?? [];
      const zs = paperZoneStateRef.current;
      const rawKind = zs?.homographyKind ?? 'none';
      const kind: PartialHomographyKind =
        rawKind === 'full' ? 'full'
        : (rawKind === 'affine' || rawKind === 'calibration') ? 'affine'
        : 'none';
      const conf = zs?.homographyConfidence ?? 0;
      const signedYaw = GAZE_YAW_FLIP ? inp.yaw : -inp.yaw;
      // Coarse gaze target point from head yaw/pitch (direction only, approx).
      const gazeTarget = {
        x: clamp01(0.5 + Math.sin((signedYaw * Math.PI) / 180) * 0.6),
        y: clamp01(0.5 + Math.max(-0.5, Math.min(0.5, -inp.pitch / 40)) * 0.6),
      };
      const registry = buildTargetRegistry({
        t: now,
        paper: kind !== 'none'
          ? { center: { x: 0.5, y: 0.77 }, homographyKind: kind, homographyConfidence: conf }
          : null,
        objects: objs,
      });
      const gaze = registry.targets.length
        ? gazeTargetLikelihood(gazeTarget, registry)
        : null;
      // Conditional decode (0704 §9 / gap ⑮) — interpret the gazed target by
      // task context. FPR lever: a book glance is normal in open-book but
      // surfaced in closed-book; an uncertain gaze is suppressed. Observation
      // only, never a verdict.
      const dwellMs = now - (obsDwellPrevRef.current?.t ?? now);
      const interp = gaze
        ? interpretGazeEvent(candidateFromLikelihood(gaze, registry, dwellMs), { examMode: EXAM_MODE })
        : null;
      const x = buildObservationVector({
        t: now,
        sensors: { gazeZ: 0, keyboardZ: 0, voiceSDR: 0, stylusZ: 0, bodyZ: 0 },
        paperZone: zs?.zone ?? null,
        homographyKind: kind,
        homographyConfidence: conf,
        gaze,
        objects: objs,
        context: { examMode: EXAM_MODE },
        // This tap does not read the engine's sensor z-scores → flag missing.
        missingChannels: { gaze: true, keyboard: true, voice: true, stylus: true, body: true },
      });
      setObsVec(x);
      setObsInterp(interp);
      // Per-state dwell histogram (HSMM duration prep) on the gaze zone.
      const gazeState = classifyGazeZone(signedYaw, inp.pitch) as string;
      const prev = obsDwellPrevRef.current;
      if (!prev) obsDwellPrevRef.current = { state: gazeState, t: now };
      else if (prev.state !== gazeState) {
        durationHistRef.current.record(prev.state, now - prev.t);
        obsDwellPrevRef.current = { state: gazeState, t: now };
      }
      (window as any).__drsimonObs = {
        x, registry, interp, durations: durationHistRef.current.summary(),
      };
      // eslint-disable-next-line no-console
      console.log('[obsVec]', {
        targets: registry.targets.length,
        gazeTop: gaze?.top?.type, entropyBits: (gaze?.entropy ?? 0).toFixed(2),
        zone: x.paperZone, homog: `${x.homographyKind} ${(conf * 100).toFixed(0)}%`,
        obj: x.numObjects, coast: x.numCoasting,
        phone: x.phoneVisible, pen: x.penVisible, book: x.bookVisible,
        inter: x.handObjectInteraction,
        examMode: EXAM_MODE,
        decode: interp ? `${interp.action}/${interp.basis}` : '-',
        label: interp?.neutralLabel,
      });
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 3-A — HEAD_POSE_UNKNOWN tracking. Slide 9: when face is lost
  // the pose-fallback cone is noisy — silently drawing it is "정직하지
  // 못하다" and creates a defensibility hole. We stop drawing the ray
  // and surface a badge / narrator event instead.
  const headPoseLastSeenRef = useRef<number>(performance.now());
  const [headPoseUnknownSec, setHeadPoseUnknownSec] = useState(0);
  const [headPoseUnknownEvents, setHeadPoseUnknownEvents] = useState<Array<{ ts: number; dur: number }>>([]);
  const headPoseWasUnknownRef = useRef(false);
  const headPoseUnknownStartRef = useRef<number | null>(null);
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = performance.now();
      if (face.hasFace) headPoseLastSeenRef.current = now;
      const unkSec = (now - headPoseLastSeenRef.current) / 1000;
      setHeadPoseUnknownSec(unkSec);
      const nowUnk = unkSec >= 1.0;  // 1s grace before declaring unknown
      if (nowUnk && !headPoseWasUnknownRef.current) {
        headPoseUnknownStartRef.current = Date.now();
        recordEvidence('gaze', 'med', 'HEAD_POSE_UNKNOWN start', false);
      } else if (!nowUnk && headPoseWasUnknownRef.current && headPoseUnknownStartRef.current) {
        const ts = headPoseUnknownStartRef.current;
        const dur = (Date.now() - ts) / 1000;
        setHeadPoseUnknownEvents(es => [...es.slice(-9), { ts, dur }]);
        headPoseUnknownStartRef.current = null;
        const risk: RiskLevel = dur >= 5 ? 'high' : 'med';
        recordEvidence('gaze', risk, `HEAD_POSE_UNKNOWN resolved (${dur.toFixed(1)}s)`, true);
      }
      headPoseWasUnknownRef.current = nowUnk;
    }, 200);
    return () => window.clearInterval(id);
  }, [face.hasFace]);

  // Phase 2-F — Dyadic framing analysis. Computed per multiFace update.
  // Falls back to the single-face bbox when multiFace yields 0 faces but
  // useFaceLandmarker still has one, so the verdict reflects single-face
  // capacity advice ("ready for 2nd person", etc.).
  const framing: FramingResult = (() => {
    if (multiFace.faces.length > 0) {
      return analyzeFraming(multiFace.faces.map(f => f.bbox));
    }
    // Single-face fallback from useFaceLandmarker via faceBboxRef
    const fb = faceBboxRef.current;
    if (fb) {
      return analyzeFraming([{
        x: fb.minX, y: fb.minY,
        w: fb.maxX - fb.minX, h: fb.maxY - fb.minY,
      }]);
    }
    return analyzeFraming([]);
  })();
  // Keep a ref so the JA effect doesn't restart on every ROI edit.
  const allRoisRef = useRef<Roi[]>([paperRoi, ...customRois]);
  allRoisRef.current = [paperRoi, ...customRois];

  useEffect(() => {
    const id = setInterval(() => {
      const now = performance.now();
      const frame = jaDetectorRef.current.update(
        multiFace.faces, allRoisRef.current, now,
      );
      setJaFrame(frame);
      // Phase 2-C: push (ts, state) sample, prune 60s+ tail.
      const buf = jaTimelineRef.current;
      buf.push({ ts: now, state: frame.dyadState });
      const cutoff = now - 60_000;
      while (buf.length && buf[0].ts < cutoff) buf.shift();
    }, 100);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiFace.faces]);

  const pose = usePoseLandmarker(COMPOSED_VIDEO_ID);
  poseHeadRef.current = pose.head;
  poseAuxRef.current = { ...pose.head, shoulderAngle: pose.shoulderAngle, rawLandmarks: pose.rawLandmarks };

  // Feed the downPosture detector with current pose state every render.
  //   gazeEndpointY: project from nose downward by head pitch. Pitch
  //                  convention is negative-down, so -sin(pitchRad)
  //                  yields a positive Y delta (further down on screen)
  //                  when the user is looking down.
  //   paperBottomY: max Y of homography corners (paper bottom edge).
  const pitchRad = ((pose.head.pitch ?? 0) * Math.PI) / 180;
  const gazeEndpointY = (pose.head.noseY ?? 0.3) + (-Math.sin(pitchRad)) * 0.5;
  downPostureInputRef.current = {
    pitch: pose.head.pitch ?? 0,
    landmarks: pose.rawLandmarks,
    gazeEndpointY,
    paperBottomY: Math.max(
      PAPER_CORNERS_DEFAULT[2].y, PAPER_CORNERS_DEFAULT[3].y,
    ),
  };

  const obj = useObjectDetector(COMPOSED_VIDEO_ID, true);
  detectionsRef.current = obj.detections;

  const hand = useHandLandmarker(COMPOSED_VIDEO_ID, HAND_OVERLAY_ID, true, faceBboxRef);

  // Phase 3-B/D — late-bound inputs refresh (set every render).
  phase3InputsRef.current = {
    handLandmarks: hand.landmarks ?? null,
    downPostureActive: downPosture.active,
    downPostureFrequent: downPosture.escalation === 'frequent',
    headUnknownSec: headPoseUnknownSec,
    mgActive: jaFrame.mutualGazes.some(m => m.sustained),
    jaActive: jaFrame.jointAttentions.length > 0,
    faceHasFace: face.hasFace,
  };
  phoneGripRef.current = { grip: hand.phoneGrip, sustainFrames: hand.gripSustainFrames };

  const [partial, setPartial] = useState<PartialGazeEstimate | null>(null);
  useEffect(() => {
    const id = setInterval(() => {
      if (!pose.rawLandmarks) { setPartial(null); return; }
      const est = classifyPartialVisibility({
        poseLandmarks: pose.rawLandmarks,
        shoulderAngle: pose.shoulderAngle ?? 0,
      });
      setPartial(est);
    }, 150);
    return () => clearInterval(id);
  }, [pose.rawLandmarks, pose.shoulderAngle]);

  const [armFallback, setArmFallback] = useState<ArmFallbackResult>({
    active: false, primary: null, arms: [], narratorText: '', uiColor: 'green',
  });
  const [armObjects, setArmObjects] = useState<ArmObjectIntersection[]>([]);
  useEffect(() => {
    const id = setInterval(() => {
      const result = classifyArmFallback({
        handDetected: hand.detected,
        poseLandmarks: pose.rawLandmarks ?? null,
      });
      setArmFallback(result);
      setArmObjects(intersectArmWithObjects(result, obj.detections));
    }, 300);  // hand-lost fallback — slow-changing; 200→300 (light cleanup)
    return () => clearInterval(id);
  }, [hand.detected, pose.rawLandmarks, obj.detections]);

  // ── Pen object detection (image processing in hand ROI) ──────────────
  // Verifies actual presence of an elongated thin object, not just a
  // pen-like grip pose. CEO 0520 segmentation feedback applied — pure
  // hand geometry cannot distinguish pen-holding from paper-pinching.
  useEffect(() => {
    if (!camReady) return;
    let stopped = false;
    if (!penDetectCanvasRef.current) {
      penDetectCanvasRef.current = document.createElement('canvas');
    }
    if (!phoneDetectCanvasRef.current) {
      phoneDetectCanvasRef.current = document.createElement('canvas');
    }
    if (!handObjCanvasRef.current) {
      handObjCanvasRef.current = document.createElement('canvas');
    }
    preloadHandObjectClassifier();  // start downloading the ONNX model
    const tick = () => {
      if (stopped) return;
      const v = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement | null;
      const lms = hand.landmarks;
      if (!v || !lms) {
        setPenObject({ present: false, confidence: 0, handBbox: { x: 0, y: 0, w: 0, h: 0 } });
        setPhoneObject({ present: false, confidence: 0, handBbox: { x: 0, y: 0, w: 0, h: 0 } });
        setHandObj({ dominant: 'none', confidence: 0,
                     noneProb: 0, penProb: 0, phoneProb: 0,
                     handBbox: { x: 0, y: 0, w: 0, h: 0 } });
        return;
      }
      setPenObject(detectPenObject(lms, v, penDetectCanvasRef.current!));
      setPhoneObject(detectPhoneObject(lms, v, phoneDetectCanvasRef.current!));
      // Async ML — fire and forget; UI uses the most recent result.
      classifyHandObject(lms, v, handObjCanvasRef.current!).then(setHandObj).catch(() => {});
    };
    const id = window.setInterval(tick, 200);
    tick();
    return () => { stopped = true; window.clearInterval(id); };
  }, [camReady, hand.landmarks]);

  // 3kukjee paper clip detection
  const [paperClip, setPaperClip] = useState<PaperClipResult>({
    isClipped: false, clippedCorners: [], visibleFraction: 1,
    severity: 'none', narratorText: '', uiColor: 'green',
  });
  // Ref mirror so interval closures (paper-zone transition logger) can read
  // the current paper visibility without re-subscribing.
  const paperClipRef = useRef<PaperClipResult | null>(null);
  paperClipRef.current = paperClip;
  // Last logged gaze∩paper zone, for throttled time-series logging of the
  // 3-zone (안/경계/밖) state into the evidence log (CEO 0422 피드백).
  const paperZoneLastRef = useRef<{ zone: string; t: number } | null>(null);
  useEffect(() => {
    const id = setInterval(() => {
      setPaperClip(detectPaperClip(paperCornersRef.current));
    }, 300);
    return () => clearInterval(id);
  }, []);

  // Multi-paper detection (runs at 2 Hz)
  const multiPaperCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [multiPaper, setMultiPaper] = useState<MultiPaperResult>({
    secondaryCount: 0, secondaryCenters: [], confidence: 0,
    narratorText: '추가 시험지 없음', uiColor: 'green',
  });
  useEffect(() => {
    const canvas = document.createElement('canvas');
    multiPaperCanvasRef.current = canvas;
    const id = setInterval(() => {
      const vid = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement | null;
      if (!vid || !vid.videoWidth || !multiPaperCanvasRef.current) return;
      setMultiPaper(detectMultiPaper(vid, paperCornersRef.current, multiPaperCanvasRef.current));
    }, 500);
    return () => clearInterval(id);
  }, []);

  // Phase 3-C — primary selector tick (declared here so multiPaper is in scope).
  useEffect(() => {
    const id = window.setInterval(() => {
      const cands = [
        { id: 'primary', bbox: paperRoi.bbox },
        ...multiPaper.secondaryCenters.map((c, i) => ({
          id: `secondary_${i}`,
          bbox: { x: Math.max(0, c.x - 0.15), y: Math.max(0, c.y - 0.15), w: 0.30, h: 0.30 },
        })),
      ];
      const noseY = pose.head.noseY ?? 0.3;
      const noseX = pose.head.noseX ?? 0.5;
      const dx = (pose.head.yaw ?? 0) / 60;
      const dy = -(pose.head.pitch ?? 0) / 60;
      const landing = pose.bodyVisible ? {
        x: Math.max(0, Math.min(1, noseX + dx)),
        y: Math.max(0, Math.min(1, noseY + dy)),
      } : null;
      setPrimarySel(primarySelRef.current.update(landing, cands, performance.now()));
    }, 400);  // 5s-window selector — slow-changing; 200→400 (light cleanup)
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiPaper.secondaryCenters]);

  // Object bbox + arm zone canvas overlay (draws every rAF).
  const armFallbackRef = useRef(armFallback);
  armFallbackRef.current = armFallback;
  const phoneObjectRef = useRef(phoneObject);
  phoneObjectRef.current = phoneObject;
  // Pose-shape ref consumed by the OBJECT_OVERLAY rAF loop. Updated below
  // after pinchLike / wrapLike are computed in the render scope.
  const phonePoseRef = useRef({ wrapLike: false, pinchLike: false });
  // Pose-skeleton + downPosture refs for the OBJECT_OVERLAY rAF loop.
  const poseSkelRef = useRef<typeof pose.rawLandmarks>(null);
  poseSkelRef.current = pose.rawLandmarks;
  const downPostureDrawRef = useRef<DownwardPostureResult>(downPosture);
  downPostureDrawRef.current = downPosture;
  // Consumed by OBJECT_OVERLAY draw loop — used to suppress EfficientDet
  // phone bbox when ML is confident the held object is NOT a phone.
  const handObjDrawRef = useRef<HandObjectResult>(handObj);
  handObjDrawRef.current = handObj;

  useEffect(() => {
    let animId = 0;
    const drawFrame = () => {
      animId = requestAnimationFrame(drawFrame);
      const canvas = document.getElementById(OBJECT_OVERLAY_ID) as HTMLCanvasElement | null;
      const video = document.getElementById(RAW_VIDEO_ID) as HTMLVideoElement | null;
      if (!canvas || !video || !video.videoWidth) return;
      if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
      if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const vw = canvas.width;
      const vh = canvas.height;
      ctx.clearRect(0, 0, vw, vh);
      // We draw RECTANGLES in mirrored canvas space (so they align with
      // the mirrored video), but TEXT labels are drawn back in identity
      // space at the corresponding mirrored visual coordinate — that
      // way labels read left-to-right normally instead of backwards.
      const drawLabel = (txt: string, visualX: number, visualY: number,
                         bgColor: string) => {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.font = 'bold 13px sans-serif';
        const tw = ctx.measureText(txt).width + 8;
        ctx.fillStyle = bgColor;
        ctx.fillRect(visualX, visualY - 20, tw, 20);
        ctx.fillStyle = '#fff';
        ctx.fillText(txt, visualX + 4, visualY - 5);
        ctx.restore();
      };

      ctx.save();
      ctx.translate(vw, 0);
      ctx.scale(-1, 1);

      let efficientDetPhoneShown = false;  // suppress dup PhoneObject box
      // ML veto: when the trained classifier confidently says the held
      // object is not a phone, suppress the EfficientDet phone box —
      // EfficientDet-Lite0 routinely hallucinates 'cell phone' on pens at
      // 0.35–0.45, and the user explicitly flagged the resulting box on
      // pen-holding frames in the 0524 12:58 video.
      const hd = handObjDrawRef.current;
      // ML veto: classifier confidently not-phone OR leaning pen suppresses the
      // EfficientDet phone box. 2026-06-22 user report: a held PEN still drew a
      // phantom 'cell phone' box when ML dipped below the 0.55 bar — so a pen
      // lean (≥0.40) now also vetoes.
      const mlSaysNotPhone =
        (hd.confidence >= 0.55 && hd.dominant !== 'phone') ||
        (hd.dominant === 'pen' && hd.confidence >= 0.40);
      // v2 main-path routing: draw ALL tracked exam objects (stable id, class
      // vote, occlusion coasting) instead of raw per-frame detections — boxes
      // no longer flicker and persist (dashed) while a hand briefly occludes
      // them. Shows the tracking/memory layer working across objects (phone /
      // book / laptop / pen…). Whitelist avoids cup/clock/person clutter.
      const DRAW_LABELS = new Set([
        'cell phone', 'book', 'laptop', 'remote', 'scissors', 'pen', 'pencil',
      ]);
      for (const t of trackedObjectsDrawRef.current) {
        if (!DRAW_LABELS.has(t.label)) continue;
        if (t.score < 0.50) continue;
        const isPhone = t.label === 'cell phone';
        // EfficientDet-Lite0 hallucinates 'cell phone' on pens at ~0.4–0.55;
        // require a higher floor for the phone class so phantom boxes on a
        // held pen don't draw (2026-06-22 user report).
        if (isPhone && t.score < 0.60) continue;
        if (isPhone && mlSaysNotPhone) continue;   // phone-only ML veto
        const coasting = t.state === 'coasting';
        // phone=red, other exam objects=cyan; coasting=yellow dashed.
        const color = coasting ? '#eab308' : (isPhone ? '#ef4444' : '#06b6d4');
        const x = t.bbox.x * vw;
        const y = t.bbox.y * vh;
        const w = t.bbox.w * vw;
        const h = t.bbox.h * vh;
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        if (coasting) ctx.setLineDash([6, 4]);
        ctx.strokeRect(x, y, w, h);
        if (coasting) ctx.setLineDash([]);
        // Visual position of the rect's LEFT edge (after mirror) is vw - (x + w)
        const visualLeft = vw - (x + w);
        const interTxt = t.interaction === 'held' ? ' ·파지'
          : t.interaction === 'occluded_by_hand' ? ' ·손가림'
          : t.interaction === 'contact' ? ' ·접촉' : '';
        drawLabel(`${t.labelKo || t.label} ${(t.score * 100).toFixed(0)}% #${t.id}${interTxt}`
          + (coasting ? ' (추적)' : ''), visualLeft, y, color + 'cc');
        if (isPhone) efficientDetPhoneShown = true;
      }
      // Suppress the image-proc PhoneObjectDetector box when EfficientDet
      // has already drawn its 'cell phone' box on the same scene — they
      // overlap on the same object and the dashed duplicate just clutters
      // the demo (CEO 0523 00:31 feedback).
      const po = phoneObjectRef.current;
      const ph = phonePoseRef.current;
      if (po.present && ph.wrapLike && !ph.pinchLike && !efficientDetPhoneShown) {
        const hb = po.handBbox;
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 3;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(hb.x * vw, hb.y * vh, hb.w * vw, hb.h * vh);
        ctx.setLineDash([]);
        const visualLeft = vw - (hb.x * vw + hb.w * vw);
        drawLabel(`핸드폰 ${(po.confidence * 100).toFixed(0)}% (img)`,
                  visualLeft, hb.y * vh, '#ef4444cc');
      }
      // ── Pose skeleton overlay ──────────────────────────────────────
      // Draws shoulder/elbow/wrist/hip connections so the user can see
      // exactly which body landmarks the downPosture detector is using.
      const lm = poseSkelRef.current;
      if (lm && lm.length >= 25) {
        const downPostureActive = downPostureDrawRef.current.active;
        const downPostureBuilding = downPostureDrawRef.current.score >= 0.4;
        const skColor = downPostureActive ? '#ef4444'
          : downPostureBuilding ? '#eab308'
          : '#22c55e';
        ctx.strokeStyle = skColor;
        ctx.fillStyle = skColor;
        ctx.lineWidth = 3;
        const seg = (a: number, b: number) => {
          const A = lm[a]; const B = lm[b];
          if (!A || !B) return;
          if ((A.visibility ?? 1) < 0.3 || (B.visibility ?? 1) < 0.3) return;
          ctx.beginPath();
          ctx.moveTo(A.x * vw, A.y * vh);
          ctx.lineTo(B.x * vw, B.y * vh);
          ctx.stroke();
        };
        const dot = (i: number, r: number) => {
          const p = lm[i];
          if (!p || (p.visibility ?? 1) < 0.3) return;
          ctx.beginPath();
          ctx.arc(p.x * vw, p.y * vh, r, 0, Math.PI * 2);
          ctx.fill();
        };
        // Shoulders, torso, arms
        seg(11, 12);          // shoulders
        seg(11, 13); seg(13, 15);  // left arm
        seg(12, 14); seg(14, 16);  // right arm
        seg(11, 23); seg(12, 24);  // torso sides
        seg(23, 24);          // hips
        [11, 12, 13, 14, 15, 16, 23, 24].forEach(i => dot(i, 4));

        // Posture-state text label in the skeleton color, so green/yellow/red
        // is self-explanatory (2026-06-23 user report: "초록/빨강 언제인지 모르겠다").
        //   초록=정상 · 노랑=하방 형성중(score≥0.4) · 빨강=하방 자세(고개숙임+상체회전)
        {
          const _ls = lm[11]; const _rs = lm[12];
          if (_ls && _rs && (_ls.visibility ?? 1) > 0.3 && (_rs.visibility ?? 1) > 0.3) {
            const midPx = ((_ls.x + _rs.x) / 2) * vw;
            const topPx = Math.min(_ls.y, _rs.y) * vh;
            const stateTxt = downPostureActive ? '자세: 하방 (고개숙임·상체회전)'
              : downPostureBuilding ? '자세: 하방 형성중'
              : '자세: 정상';
            drawLabel(stateTxt, Math.max(2, vw - midPx - 55), Math.max(22, topPx - 10), skColor + 'cc');
          }
        }

        // Down-posture active extras: arrow showing torso twist direction +
        // glowing badge anchored to the leaning shoulder.
        if (downPostureActive) {
          const ls = lm[11]; const rs = lm[12];
          if (ls && rs && (ls.visibility ?? 1) > 0.3 && (rs.visibility ?? 1) > 0.3) {
            const cxN = (ls.x + rs.x) / 2;
            const cyN = (ls.y + rs.y) / 2;
            const cx = cxN * vw; const cy = cyN * vh;
            // Down-arrow indicator
            ctx.strokeStyle = '#ef4444';
            ctx.lineWidth = 5;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx, cy + vh * 0.15);
            ctx.stroke();
            // Arrowhead
            ctx.beginPath();
            ctx.moveTo(cx - 12, cy + vh * 0.15 - 12);
            ctx.lineTo(cx, cy + vh * 0.15);
            ctx.lineTo(cx + 12, cy + vh * 0.15 - 12);
            ctx.stroke();
          }
        }
      }

      const af = armFallbackRef.current;
      if (af.active && af.primary) {
        const hz = af.primary.estimatedHandZone;
        ctx.strokeStyle = '#a855f7';
        ctx.lineWidth = 2.5;
        ctx.setLineDash([8, 4]);
        ctx.strokeRect(hz.x * vw, hz.y * vh, hz.w * vw, hz.h * vh);
        ctx.setLineDash([]);
        const visualLeft = vw - (hz.x * vw + hz.w * vw);
        drawLabel('arm est. ' + (af.primary.confidence * 100).toFixed(0) + '%',
                  visualLeft, hz.y * vh + 34, '#a855f7cc');
      }
      ctx.restore();
    };
    animId = requestAnimationFrame(drawFrame);
    return () => cancelAnimationFrame(animId);
  }, []);
  const [, setTick] = useState(0);
  useEffect(() => {
    lastSampleRef.current = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      const dt = Math.min(500, now - lastSampleRef.current);
      lastSampleRef.current = now;
      const z = paperZoneStateRef.current?.zone;
      if (z) {
        dwellRef.current.total += dt;
        if (z === 'confident_inside') dwellRef.current.inside += dt;
        else if (z === 'boundary') dwellRef.current.boundary += dt;
        else dwellRef.current.outside += dt;
        // Time-series log of the gaze∩paper zone — transitions only,
        // throttled ≥600 ms. Records prior zone + its dwell duration so the
        // export carries a "시험지 안/경계/밖 + duration" log (CEO 0422).
        const pl = paperZoneLastRef.current;
        if (!pl || (pl.zone !== z && now - pl.t >= 600)) {
          if (pl) {
            const dur = (now - pl.t) / 1000;
            const label = z === 'confident_inside' ? '시험지 안(확실)'
              : z === 'boundary' ? '경계' : '시험지 밖(확실)';
            const off = paperZoneStateRef.current?.offScreen ? ' · 화면 밖' : '';
            const vis = paperClipRef.current
              ? ` · 가시성 ${(paperClipRef.current.visibleFraction * 100).toFixed(0)}%` : '';
            const risk: RiskLevel = z === 'confident_outside' ? 'med' : 'low';
            recordEvidence('gaze', risk,
              `[raw] 시험지 영역: ${label}${vis}${off} (이전 ${pl.zone} ${dur.toFixed(1)}s)`,
              modalityInputRef.current.faceVisible, 'raw', 'paper_zone');
          }
          paperZoneLastRef.current = { zone: z, t: now };
        }
      }
      setTick(t => t + 1);
    }, 100);
    return () => clearInterval(id);
  }, []);

  const resetDwell = () => {
    dwellRef.current = { inside: 0, boundary: 0, outside: 0, total: 0 };
    lastSampleRef.current = performance.now();
  };

  const dw = dwellRef.current;
  const pct = (n: number) => dw.total > 0 ? (n / dw.total * 100) : 0;

  // Hand-pose gating — pen vs phone are mutually exclusive in grip shape:
  //   pen grip   = thumb pinches index/middle (close tips). Fires
  //                detectPenGrip with confidence > 0.3.
  //   phone grip = cylindrical wrap, thumb on opposite side of object,
  //                NOT pinching. Fires detectPhoneGrip with confidence > 0.3.
  // We use this geometric distinction to disambiguate image-processing
  // false positives (CEO 0522 19:43 feedback: "핸드포즈랑 잡은 물체를
  // 같이 확인해봐요").
  const pinchLike = hand.penGrip.isGrip && hand.penGrip.confidence > 0.30;
  const wrapLike  = hand.phoneGrip.isGrip && hand.phoneGrip.confidence > 0.30;
  phonePoseRef.current = { pinchLike, wrapLike };

  // Raised from 0.25 → 0.50 — 0524 12:58 video had EfficientDet fire
  // 'cell phone' at score 0.41 on a PEN, drawing a red "핸드폰 41%" box
  // on top of the pen. Real phones in the same sessions fire at ≥ 0.50,
  // so this threshold keeps recall on actual phones while clipping the
  // weak false positives that confused the demo.
  // v2 main-path routing: source from the ObjectTracker (stable identity,
  // class vote, occlusion coasting) instead of raw per-frame detections —
  // a phone track stays valid while a hand briefly occludes it, so
  // heldObject doesn't drop the phone mid-grip.
  const phoneTrack = trackedObjectsDrawRef.current.find(
    (t) => t.label === 'cell phone' && t.score >= 0.50,
  );
  const efficientDetPhone = !!phoneTrack;

  // ── Single-class hand-held object classifier ─────────────────────
  // Signal hierarchy (trust descending):
  //   1. EfficientDet 'cell phone'  — strong evidence (model)
  //   2. PenObjectDetector present   — image-proc, aspect≥4 → pen-thin
  //   3. PhoneObjectDetector present — image-proc, aspect 1.2~4 → phone-brick
  //   4. Hand pose (pinch vs wrap)   — geometry-only, weak
  // First positive signal wins. Image evidence (1–3) always overrides hand
  // pose (4) — the 0522 19:43~20:02 false positives were caused by trusting
  // pose over image evidence (phone-holding pose fired detectPenGrip).
  type HeldClass = 'phone' | 'pen' | 'none';
  // Pen aspect (1.29–4.40) overlaps PHONE aspect (1.14–3.11) in image
  // processing — they CANNOT be reliably distinguished by dark-mass shape
  // alone. Verified across 9 phone-holding and 24 pen-holding frames in
  // sim. We therefore drop PenObjectDetector from classification (kept
  // only as a HUD display signal) and lean on hand pose + writing motion
  // for pen, multi-source for phone.
  // Classifier hierarchy after the 0523 v4 fix (ONNX external-data bug
  // resolved → ML now actually runs in browser). 'pen vs phone' is a
  // genuinely hard problem for image-proc alone, so ML is the primary
  // discriminator; pose + image-proc are fallbacks.
  const ML_CONF_THRESHOLD = 0.50;
  const heldInstant: HeldClass = (() => {
    // 0524 fix: ML "strongly says not phone" beats a weak EfficientDet
    // 'cell phone' positive. Without this rule, a 41 %-confidence
    // EfficientDet hit on a pen overrode an ML prediction of 'none' at
    // 93 % and forced the writingState to phone-grip on a pen-holding
    // hand. Threshold 0.60 = "ML is confident enough to overrule the
    // pre-trained detector."
    if (handObj.confidence >= 0.60 && handObj.dominant !== 'phone') {
      return handObj.dominant;
    }
    // 1. EfficientDet 'cell phone' — strong evidence (≥ 0.50)
    if (efficientDetPhone) return 'phone';
    // 2. ML classifier (only when sufficiently confident)
    if (handObj.confidence >= ML_CONF_THRESHOLD) return handObj.dominant;
    // 3. Phone wrap pose — geometrically specific; rare false fire on pens
    if (wrapLike) return 'phone';
    // 4. Pen + writing motion — writing speed is pen-specific (rarely seen
    //    on phone hold). Most reliable pen signal in the absence of ML.
    if (pinchLike && hand.isWriting) return 'pen';
    // 5. Image-proc phone with decent confidence — fires when the dark
    //    mass in the hand looks like a phone brick.
    if (phoneObject.present && phoneObject.confidence >= 0.5) return 'phone';
    // 6. Plain pinch without writing motion — could be pen idle OR phone
    //    grip. Without supporting evidence, mark 'none' to avoid wrong
    //    answers; the demo prefers honest uncertainty over false positives.
    if (pinchLike) return 'none';
    return 'none';
  })();
  // Temporal onset gate + dropout hysteresis (see HELD_GATE_ENABLED).
  //   Rising edge  — a NEW positive class must persist HELD_ONSET_CONFIRM_MS
  //                  before it is committed, so a single spurious frame
  //                  (pen briefly read as phone) never becomes an alert.
  //   Falling edge — once committed, the class is held HELD_DROPOUT_HOLD_MS
  //                  after it stops firing so detection doesn't flicker as
  //                  confidence wobbles near threshold (CEO 0522 20:02:
  //                  "검출 빈도 낮음").
  const heldCandidateRef = useRef<{ kind: HeldClass; since: number }>({ kind: 'none', since: 0 });
  const heldCommittedRef = useRef<{ kind: HeldClass; ts: number }>({ kind: 'none', ts: 0 });
  {
    const now = performance.now();
    // Expire a stale commit first, so a re-appearance must re-pass the
    // onset gate rather than instantly resurrecting the old class.
    if (heldCommittedRef.current.kind !== 'none'
        && now - heldCommittedRef.current.ts >= HELD_DROPOUT_HOLD_MS) {
      heldCommittedRef.current = { kind: 'none', ts: 0 };
    }
    if (heldInstant !== heldCandidateRef.current.kind) {
      heldCandidateRef.current = { kind: heldInstant, since: now };
    }
    if (heldInstant !== 'none') {
      // Same class as the live commit → refresh immediately (extends the
      // dropout hold). A different class must clear the onset window first.
      const sameAsCommitted = heldInstant === heldCommittedRef.current.kind;
      const onsetMs = HELD_GATE_ENABLED ? HELD_ONSET_CONFIRM_MS : 0;
      const confirmed = sameAsCommitted
        || now - heldCandidateRef.current.since >= onsetMs;
      if (confirmed) heldCommittedRef.current = { kind: heldInstant, ts: now };
    }
  }
  const heldObject: HeldClass = heldCommittedRef.current.kind;
  const phoneObjVisibleForState = heldObject === 'phone';

  // Feed the per-modality semantic tick (late-bound via ref to avoid
  // forward-declaration ordering — heldObject is computed here).
  modalityInputRef.current = {
    yaw: pose.head.yaw ?? 0,
    pitch: pose.head.pitch ?? 0,
    shoulderAngle: pose.shoulderAngle ?? 0,
    held: heldObject,
    faceVisible: face.hasFace,
  };

  // writingState — derived from heldObject (primary hand) + secondary
  // hand pose for bimanual cases. The 'phone-2hand' label is reserved
  // for when BOTH hands hold phones; one-phone + one-pen is shown as
  // 'pen+phone' to avoid the misleading "both are phones" badge that
  // appeared in the 0523 00:54 demo.
  const secondaryHasPen   = hand.secondary?.penGrip.isGrip ?? false;
  const secondaryHasPhone = hand.secondary?.phoneGrip.isGrip ?? false;
  const writingState =
    !hand.detected ? 'no-hand'
      : heldObject === 'phone' && hand.handCount >= 2 && secondaryHasPen
        ? 'pen+phone'
        : heldObject === 'phone' && hand.handCount >= 2 && secondaryHasPhone
          ? 'phone-2hand'
          : heldObject === 'phone'
            ? 'phone-grip'
            : heldObject === 'pen' || (hand.penGrip.isGrip && hand.penGripSustainFrames >= 1)
              ? (hand.isWriting ? 'pen-grip-writing' : 'pen-grip-idle')
              : hand.isWriting ? 'writing'
                : 'idle';

  // ── Paper-centric relations (review 2026-06-06: surface 핵심 정보) ──
  // hand-over-paper + phone-near-paper. Paper position/visibility/gaze-in-out
  // already exist (paperCorners / paperClip / paperZoneState) — consolidated
  // into the "시험지 상태" HUD block below.
  const handPaper = handOverPaper(hand.landmarks ?? null, paperCornersRef.current);
  // Use the tracked phone (stable through occlusion) for the paper relation.
  const phonePaper = phoneNearPaper(phoneTrack?.bbox ?? null, paperCornersRef.current);

  const zone = paperZoneStateRef.current?.zone;
  const paperBorderColor =
    zone === 'confident_inside' ? '#22c55e'
    : zone === 'boundary' ? '#eab308'
    : zone === 'confident_outside' ? '#ef4444'
    : 'transparent';
  const stateBadgeColor =
    writingState === 'pen-grip-writing' ? '#22c55e'
    : writingState === 'pen-grip-idle' ? '#06b6d4'
    : writingState === 'phone-grip' || writingState === 'phone-2hand'
      || writingState === 'pen+phone' ? '#ef4444'
    : writingState === 'writing' ? '#eab308'
    : '#6b7280';
  const stateBadgeText =
    writingState === 'pen-grip-writing' ? 'pen+write'
    : writingState === 'pen-grip-idle' ? 'pen grip'
    : writingState === 'phone-grip' ? 'PHONE GRIP'
    : writingState === 'phone-2hand' ? 'PHONE 2-HAND'
    : writingState === 'pen+phone' ? 'PEN + PHONE'
    : writingState === 'writing' ? 'writing'
    : writingState === 'idle' ? 'idle'
    : 'no hand';

  // Phone-grip detection on hand geometry alone false-positives on
  // paper-holding (fingers wrap around paper edge, looks like a cylinder
  // grip). Gate any "phone" kind on the actual EfficientDet phone object
  // being visible — CEO 0520 segmentation feedback applied at the bimanual
  // layer. When no phone object is present, downgrade phone-coord → pen.
  const phoneObjectVisible = obj.detections.some(
    (d) => d.label === 'cell phone' && d.score >= 0.35,
  );
  const rawCoordKind = hand.coordinationKind;
  const coordKind =
    !phoneObjectVisible && rawCoordKind === 'pen+phone'  ? 'pen+pen'
    : !phoneObjectVisible && rawCoordKind === 'phone+phone' ? null
    : rawCoordKind;
  const coordSustain = hand.coordinationSustainFrames;
  const coordColor = coordKind === 'pen+phone' ? '#ef4444'
    : coordKind === 'phone+phone' ? '#ef4444'
    : '#eab308';
  const coordLabel = coordKind === 'pen+phone' ? 'pen+phone'
    : coordKind === 'phone+phone' ? 'phone+phone'
    : coordKind === 'pen+pen' ? 'pen+pen'
    : 'none';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#0b0d10',
      display: 'flex', color: '#e6edf3',
      font: '14px Pretendard, system-ui, sans-serif',
    }}>
      <div style={{
        position: 'relative', flex: 1, minWidth: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div className={
          'video-wrap'
          + (downPosture.escalation === 'frequent' ? ' down-posture-frequent'
            : downPosture.active ? ' down-posture-active' : '')
        } style={{
          position: 'relative', aspectRatio: '16 / 9',
          width: '100%', height: '100%',
        }}>
          <video id={RAW_VIDEO_ID} autoPlay playsInline muted
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'contain', background: '#000' }} />
          {PAPER_MODE === 'composite' && (
            <>
              <canvas id={COMPOSITE_CANVAS_ID}
                style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
                  width: 1, height: 1 }} />
              <video id={COMPOSED_VIDEO_ID_REAL} autoPlay playsInline muted
                style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
                  width: 1, height: 1 }} />
            </>
          )}

          {hand.detected && hand.center && (
            <div style={{ position: 'absolute',
              left: `${hand.center.x * 100}%`, top: `${hand.center.y * 100}%`,
              transform: 'translate(-50%, -130%)',
              padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600,
              background: '#0b0d10cc', border: `2px solid ${stateBadgeColor}`,
              color: stateBadgeColor, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
              {stateBadgeText}
            </div>
          )}

          {coordKind && coordSustain >= 3 && (
            <div style={{ position: 'absolute', top: 52, left: 12,
              padding: '4px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: '#0b0d10cc', border: `2px solid ${coordColor}`,
              color: coordColor, pointerEvents: 'none' }}>
              BIMANUAL: {coordLabel}
            </div>
          )}

          {paperClip.severity !== 'none' && (
            <div style={{ position: 'absolute', top: 12, right: 12,
              padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: '#0b0d10cc',
              border: `2px solid ${paperClip.uiColor === 'yellow' ? '#eab308' : '#ef4444'}`,
              color: paperClip.uiColor === 'yellow' ? '#fde047' : '#fca5a5',
              maxWidth: 200 }}>
              {paperClip.severity === 'major' ? 'PAPER CLIPPED' : 'paper near edge'}
            </div>
          )}

          {/* Hidden-phone-downPosture warning badge — top-right, below paper-clip.
              Color: frequent = red, active / building = yellow. */}
          {(downPosture.active || downPosture.score >= 0.4 || downPosture.escalation !== 'none') && (
            <div style={{ position: 'absolute',
              top: paperClip.severity !== 'none' ? 48 : 12, right: 12,
              padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
              background: '#0b0d10dd',
              border: `2px solid ${downPosture.escalation === 'frequent' ? '#ef4444'
                : downPosture.active ? '#ef4444' : '#eab308'}`,
              color: downPosture.escalation === 'frequent' ? '#fca5a5'
                : downPosture.active ? '#fca5a5' : '#fde047' }}>
              {downPosture.escalation === 'frequent'
                ? `반복 패턴 (${downPosture.eventCountWindow}회/60s)`
                : downPosture.active
                  ? `고개 숙임·손 비노출 지속 (${downPosture.sustainedSec.toFixed(1)}s)`
                  : `관찰 중 ${(downPosture.score*100).toFixed(0)}%`}
            </div>
          )}

          {/* Phase 3-A — HEAD POSE UNKNOWN badge. Fires after ≥1s
              without a face landmark. Replaces the silent fallback
              cone with an explicit "we don't know where the head is
              pointing" signal (Slide 9). */}
          {headPoseUnknownSec >= 1 && (
            <div style={{ position: 'absolute', top: 12, right: 12,
              padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
              background: '#0b0d10dd', border: '2px dashed #ef4444',
              color: '#fca5a5' }}>
              HEAD POSE UNKNOWN ({headPoseUnknownSec.toFixed(1)}s)
            </div>
          )}

          <div style={{ position: 'absolute', top: 12, left: 12,
            padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
            background: '#0b0d10cc',
            border: `2px solid ${paperBorderColor === 'transparent' ? '#6b7280' : paperBorderColor}`,
            color: paperBorderColor === 'transparent' ? '#6b7280' : paperBorderColor }}>
            {zone === 'confident_inside' ? 'ON PAPER'
              : zone === 'boundary' ? 'BOUNDARY'
              : zone === 'confident_outside' ? 'OFF PAPER'
              : 'INIT'}
          </div>

          <canvas id={OVERLAY_ID} style={{ position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
          <canvas id={HAND_OVERLAY_ID} style={{ position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
          <canvas id={OBJECT_OVERLAY_ID} style={{ position: 'absolute', inset: 0,
            width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
          <PaperCornerHandles
            paperCornersRef={paperCornersRef}
            paperHomographyRef={paperHomographyRef}
            calibCornersRef={calibCornersRef}
            calibHomographyRef={calibHomographyRef}
          />
          <CustomRoiOverlay
            rois={customRois}
            setRois={setCustomRois}
            drawMode={roiDrawMode}
            paperRoi={paperRoi}
          />

          {/* Phase 2-F — Edge warning overlays. When the framing
              verdict is 'tight' or 'clipped', show a red bar on the
              edge that the nearest face is touching. */}
          {(framing.verdict === 'two_tight' || framing.verdict === 'two_clipped'
            || framing.verdict === 'single_too_close') && framing.faces.map(f => {
            if (f.edgeMargin >= 0.05) return null;
            const isStrong = f.edgeMargin <= 0.01;
            const barStyle: React.CSSProperties = {
              position: 'absolute',
              background: isStrong ? '#ef4444' : '#eab308',
              opacity: 0.8, pointerEvents: 'none',
            };
            const w = isStrong ? 6 : 4;
            const sides: React.CSSProperties = (() => {
              switch (f.closestEdge) {
                case 'left':   return { left: 0, top: 0, bottom: 0, width: w };
                case 'right':  return { right: 0, top: 0, bottom: 0, width: w };
                case 'top':    return { top: 0, left: 0, right: 0, height: w };
                case 'bottom': return { bottom: 0, left: 0, right: 0, height: w };
              }
            })();
            return <div key={f.i} style={{ ...barStyle, ...sides }} />;
          })}
        </div>
      </div>
      <aside style={{
        width: 340, padding: '16px 18px',
        background: '#11151a', borderLeft: '1px solid #1f2630',
        overflowY: 'auto',
      }}>
        <h2 style={{ margin: 0, font: '700 18px Pretendard, sans-serif' }}>Live Demo</h2>
        <p style={{ opacity: 0.65, margin: '4px 0 16px', fontSize: 12 }}>
          Real webcam + synthesized paper / markers overlay.
        </p>
        {camError && <div style={{ background: '#3b1d1d', border: '1px solid #6b2a2a',
          padding: 8, borderRadius: 4, marginBottom: 12, fontSize: 12 }}>
          Camera: {camError}
        </div>}
        {!camReady && !camError && <div style={{ opacity: 0.7, fontSize: 12 }}>Acquiring camera...</div>}

        <Section title="시스템 점검 (Pre-flight / Health)">
          {(() => {
            const camOk = camReady && videoHealth.w > 0 && videoHealth.ready >= 2;
            const modelLoading = mlStatus.status === 'loading';
            const overall = (camError || mlStatus.status === 'failed') ? '🔴 문제'
              : (!camOk || modelLoading) ? '🟡 점검 중'
              : face.hasFace ? '🟢 정상' : '🟡 주의';
            const color = overall[0] === '🔴' ? '#fca5a5'
              : overall[0] === '🟡' ? '#fde047' : '#86efac';
            return (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 4 }}>{overall}</div>
                <KV k="카메라" v={camError ? `접근 실패: ${camError.substring(0, 30)}`
                  : camOk ? `정상 ${videoHealth.w}×${videoHealth.h}` : '초기화 중…'} />
                <KV k="얼굴 추적" v={face.hasFace ? '검출 중' : '미검출 — 정면을 바라봐 주세요'} />
                <KV k="ML 분류기(손-객체)" v={mlStatus.status === 'ready' ? '준비됨'
                  : mlStatus.status === 'loading' ? '로딩 중…' : '실패'} />
                {PEN_OD_ENABLED && (
                  <KV k="펜 모델" v={penYoloStatus === 'ready' ? '준비됨'
                    : penYoloStatus === 'failed' ? '없음/실패' : penYoloStatus} />
                )}
                {camError && (
                  <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 4 }}>
                    → 카메라 복구가 안 되면 수동 감독 전환이 필요할 수 있습니다.
                  </div>
                )}
                <div style={{ fontSize: 10, opacity: 0.55, marginTop: 4, lineHeight: 1.4 }}>
                  마이크 미사용(vision-only). 기기 자동복구·네트워크 점검은 운영 레이어(별도).
                </div>
              </>
            );
          })()}
        </Section>

        <Section title="Session summary (since page load)">
          {(() => {
            const elapsedSec = (Date.now() - sessionStartRef.current) / 1000;
            const min = Math.floor(elapsedSec / 60);
            const sec = Math.floor(elapsedSec % 60);
            const totalDownPostureDur = downPostureEvents.reduce((a, e) => a + e.dur, 0);
            return (
              <>
                <KV k="Session length" v={`${min}m ${sec}s`} />
                <KV k="Total downPosture events" v={String(downPostureEvents.length)} />
                <KV k="Total downPosture time" v={`${totalDownPostureDur.toFixed(1)}s`} />
                <KV k="Current escalation" v={downPosture.escalation} />
              </>
            );
          })()}
        </Section>

        <Section title="모달리티 의미 산출물 + 동시 발생">
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
            각 모달리티 raw→window→verdict 3단계 로그. 최종 산출물:
          </div>
          {(['gaze_area', 'hand', 'body'] as const).map(k => {
            const v = modalityVerdicts[k];
            const label = k === 'gaze_area' ? '게이즈영역'
              : k === 'hand' ? '핸드포즈' : '바디포즈';
            const phrase = !v || !v.active ? '관찰 중'
              : k === 'gaze_area' ? gazeAreaPhrase(v)
              : k === 'hand' ? handPhrase(v) : bodyPhrase(v);
            return <KV key={k} k={label} v={phrase} />;
          })}
          <div style={{ marginTop: 4, padding: '3px 6px', borderRadius: 3,
            background: coOcc.active ? 'rgba(239,68,68,0.18)' : '#0b0d10',
            border: `1px solid ${coOcc.active ? '#ef4444' : '#1f2630'}`,
            fontSize: 11, fontWeight: 600,
            color: coOcc.active ? '#fca5a5' : '#6b7280' }}>
            {coOcc.active ? `🔗 동시 발생: ${coOcc.labels.join(' + ')}` : '동시 발생 없음'}
          </div>
        </Section>

        <Section title="Evidence log (Slide 20 — 4종 증거)">
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
            timestamp + behaviorType + systemState + screenContext 동시 기록.
            세션 누적: <strong>{evidenceLog.length}</strong> events.
          </div>
          {evidenceLog.length === 0 ? (
            <div style={{ fontSize: 10, opacity: 0.5 }}>아직 기록된 이벤트 없음</div>
          ) : (
            <div style={{ fontSize: 10, lineHeight: 1.5, maxHeight: 140,
              overflowY: 'auto', padding: '4px 6px',
              background: '#0b0d10', borderRadius: 3, border: '1px solid #1f2630' }}>
              {evidenceLog.slice().reverse().slice(0, 8).map((e, i) => {
                const riskColor = e.risk === 'high' ? '#fca5a5'
                  : e.risk === 'med' ? '#fde047' : '#9ca3af';
                const screenFlag = e.evidence.screenContext.visibility === 'hidden'
                  || !e.evidence.screenContext.focused;
                return (
                  <div key={`${e.t}_${i}`} style={{ color: riskColor,
                    fontVariantNumeric: 'tabular-nums', display: 'flex',
                    justifyContent: 'space-between', gap: 6 }}>
                    <span>{new Date(e.t).toLocaleTimeString()}</span>
                    <span style={{ flex: 1, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ opacity: 0.55 }}>
                        {e.stage === 'fusion' ? '🔗' : e.stage === 'verdict' ? '✓'
                          : e.stage === 'window' ? '∑' : '·'}{e.stage}/{e.modality}{' '}
                      </span>
                      {e.label}
                    </span>
                    <span style={{ opacity: 0.6 }}>
                      {e.risk}{screenFlag ? ' · 🔴screen' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {evidenceLog.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginTop: 4 }}>
              <button onClick={() => {
                const blob = new Blob([JSON.stringify({
                  sessionStartIso: new Date(Date.now() - (performance.now() - monitoringStartRef.current)).toISOString(),
                  exportedAtIso: new Date().toISOString(),
                  // Session-level paper log: where the gaze fell relative to
                  // the paper over the whole session + final paper visibility.
                  paperLog: {
                    monitoredSec: +(dwellRef.current.total / 1000).toFixed(1),
                    gazeOnPaperPct: {
                      inside: +pct(dw.inside).toFixed(1),
                      boundary: +pct(dw.boundary).toFixed(1),
                      outside: +pct(dw.outside).toFixed(1),
                    },
                    paperVisibleFraction: +paperClip.visibleFraction.toFixed(3),
                    paperOffScreen: paperZoneStateRef.current?.offScreen ?? null,
                    finalZone: paperZoneStateRef.current?.zone ?? null,
                  },
                  events: evidenceLogRef.current.map(e => ({
                    atIso: new Date(e.t).toISOString(),
                    perfMs: e.perfT,
                    stage: e.stage,
                    modality: e.modality,
                    label: e.label,
                    risk: e.risk,
                    evidence: e.evidence,
                  })),
                }, null, 2)], { type: 'application/json' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `evidence-${Date.now()}.json`;
                a.click();
              }} style={{ fontSize: 10, padding: '2px 6px', background: '#1f2630',
                border: '1px solid #2d3743', color: '#9ca3af',
                borderRadius: 3, cursor: 'pointer' }}>
                JSON 내보내기
              </button>
              <button onClick={() => setEvidenceLog([])}
                style={{ fontSize: 10, padding: '2px 6px', background: '#1f2630',
                  border: '1px solid #2d3743', color: '#9ca3af',
                  borderRadius: 3, cursor: 'pointer' }}>리셋</button>
            </div>
          )}
        </Section>

        <Section title="30s 누적 패턴 (Phase 3-D)">
          {Object.entries(longWindowSnap).length === 0 ? (
            <div style={{ fontSize: 11, opacity: 0.5 }}>샘플 누적 중…</div>
          ) : Object.entries(longWindowSnap).map(([name, s]) => {
            const isHot = s.onRatio >= 0.30 || s.transitions >= 3;
            return (
              <div key={name} style={{ display: 'grid',
                gridTemplateColumns: '100px 1fr 70px', gap: 4, alignItems: 'center',
                fontSize: 11, marginTop: 2 }}>
                <span style={{ opacity: 0.85 }}>{name}</span>
                <div style={{ height: 6, background: '#1f2630', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(100, s.onRatio * 100)}%`,
                    height: '100%', background: isHot ? '#ef4444' : '#22c55e' }} />
                </div>
                <span style={{ fontSize: 10, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                  color: isHot ? '#fca5a5' : '#9ca3af' }}>
                  {(s.onMs/1000).toFixed(1)}s · ×{s.transitions}
                </span>
              </div>
            );
          })}
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 6, lineHeight: 1.4 }}>
            Slide 20: 단발 ❌ · 30초+ 윈도우 · 비율↑ 또는 transitions≥3 = hot 신호
          </div>
        </Section>

        <Section title="Palm exposure (Phase 3-B — 손바닥 비노출 지속)">
          <KV k="Area (sm)" v={(palm.smoothArea*1000).toFixed(2)+' ‰'} />
          <KV k="Exposed" v={palm.exposed ? 'yes' : 'no'} />
          <KV k="Hidden duration" v={palm.hiddenSec > 0 ? `${palm.hiddenSec.toFixed(1)}s` : '—'} />
          {palm.hiddenSustained && (
            <div style={{ fontSize: 11, marginTop: 4, padding: '4px 6px',
              background: '#ef444422', border: '1px solid #ef4444',
              borderRadius: 3, color: '#fca5a5' }}>
              손바닥 비노출 ≥1.5s 지속 (관찰)
            </div>
          )}
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, lineHeight: 1.4 }}>
            Slide 18: 손바닥 polygon (wrist+CMC+4 MCPs) shoelace 면적. 임계 5‰
          </div>
        </Section>

        <Section title="Head pose tracking (Phase 3-A)">
          <KV k="Status" v={headPoseUnknownSec >= 1
            ? `UNKNOWN (${headPoseUnknownSec.toFixed(1)}s)`
            : 'tracked'} />
          <KV k="Recent unknown events" v={String(headPoseUnknownEvents.length)} />
          {headPoseUnknownEvents.length > 0 && (
            <div style={{ fontSize: 10, marginTop: 4, opacity: 0.75 }}>
              {headPoseUnknownEvents.slice().reverse().slice(0, 3).map((e, i) => (
                <div key={e.ts} style={{ color: i === 0 ? '#fca5a5' : '#9ca3af' }}>
                  {new Date(e.ts).toLocaleTimeString()} · {e.dur.toFixed(1)}s 추적 불가
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, lineHeight: 1.4 }}>
            Slide 9: face lost ≥ 1s → fallback ray 중단 + 관찰 시그널 로깅
          </div>
        </Section>

        <Section title="Dyadic framing (camera FoV)">
          <KV k="Verdict" v={framing.verdict.replace(/_/g, ' ')} />
          <ZoneBar label="health" pct={framing.health * 100}
            color={verdictColor(framing.verdict)} />
          <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.5,
            color: verdictColor(framing.verdict) }}>
            {framing.message}
          </div>
          {framing.faces.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 10, opacity: 0.75 }}>
              {framing.faces.map(f => (
                <div key={f.i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>face_{f.i}</span>
                  <span>w={(f.widthFrac*100).toFixed(0)}% · {f.closestEdge} 여백 {(f.edgeMargin*100).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Body pose (down-posture-detector inputs)">
          <KV k="Body visible" v={pose.bodyVisible ? 'yes' : 'no'} />
          <KV k="Head pitch" v={`${(pose.head.pitch ?? 0).toFixed(1)}°`} />
          <KV k="Head yaw"   v={`${(pose.head.yaw ?? 0).toFixed(1)}°`} />
          <KV k="Shoulder ∠" v={`${(pose.shoulderAngle ?? 0).toFixed(1)}°`} />
          <KV k="Gaze end Y" v={`${(downPostureInputRef.current.gazeEndpointY ?? 0).toFixed(2)}`} />
          <KV k="Paper bottom Y" v={`${downPostureInputRef.current.paperBottomY.toFixed(2)}`} />
        </Section>

        <Section title="2-pillar: face partial visibility">
          {partial ? (
            <>
              <KV k="Case" v={partial.case.replace(/^CASE_\d+_/, '')} />
              <KV k="Confidence" v={partial.confidence} />
              <KV k="Ray rendered" v={partial.drawRay ? 'yes' : 'no'} />
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4, lineHeight: 1.4,
                color: partial.uiColor === 'green' ? '#86efac'
                  : partial.uiColor === 'yellow' ? '#fde047'
                  : partial.uiColor === 'orange' ? '#fdba74' : '#fca5a5' }}>
                {partial.narratorText}
              </div>
            </>
          ) : <div style={{ fontSize: 12, opacity: 0.55 }}>no pose data</div>}
        </Section>

        <Section title="추적 객체 (Tracked objects) — 식별자 유지">
          {PEN_OD_ENABLED && (
            <KV k="펜 YOLO 모델" v={penYoloStatus === 'ready' ? '로드됨 (ready)'
              : penYoloStatus === 'failed' ? 'pen_detect.onnx 없음/실패'
              : penYoloStatus} />
          )}
          {trackedObjects.length === 0 ? (
            <div style={{ fontSize: 10, opacity: 0.5 }}>추적 중 객체 없음</div>
          ) : trackedObjects.map(t => {
            const inter = t.interaction === 'held' ? '파지 중'
              : t.interaction === 'contact' ? '손 접촉'
              : t.interaction === 'occluded_by_hand' ? '손에 가림'
              : '—';
            return <KV key={t.id} k={`#${t.id} ${t.labelKo}`}
              v={`${t.state === 'coasting' ? '추적유지(검출X)' : '검출'} · ${inter}`} />;
          })}
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, lineHeight: 1.4 }}>
            프레임별 재인식 대신 IoU 추적 + 클래스 투표로 식별자 유지, 손 가림 시
            코스팅(클래스 보존). 손–객체 상태는 관찰만.
          </div>
        </Section>

        <Section title="시험지 상태 (Paper status) — 핵심 정보">
          <KV k="시험지 가시성" v={`${(paperClip.visibleFraction * 100).toFixed(0)}%`
            + (paperClip.severity !== 'none' ? ' · 일부 프레임 밖' : ' · 프레임 내')} />
          <KV k="시선 영역" v={zone ? (PAPER_ZONE_META[zone]?.label ?? zone) : '판정 전'} />
          <KV k="손–시험지 가림" v={handPaper.over
            ? `가림 (손 ${(handPaper.coverage * 100).toFixed(0)}%)`
            : (hand.detected ? '비가림' : '손 미검출')} />
          <KV k="폰–시험지 근접" v={phoneTrack
            ? (phonePaper.overlap ? '시험지 위/겹침'
              : phonePaper.near ? `근접 (gap ${(phonePaper.gapNorm * 100).toFixed(0)}%)`
              : '원거리')
            : '폰 미검출'} />
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, lineHeight: 1.4 }}>
            위치=paperCorners 아웃라인 · 가시성=PaperClip · 시선=PaperZone ·
            가림/근접=관찰만(해석 없음)
          </div>
        </Section>

        <Section title="3-pillar: paper clip detector">
          <KV k="Severity" v={paperClip.severity} />
          <KV k="Visible area" v={`${(paperClip.visibleFraction * 100).toFixed(0)}%`} />
          {paperClip.clippedCorners.length > 0 && (
            <KV k="Clipped" v={paperClip.clippedCorners.map(c => c.id).join(', ')} />
          )}
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4, lineHeight: 1.4,
            color: paperClip.uiColor === 'green' ? '#86efac'
              : paperClip.uiColor === 'yellow' ? '#fde047'
              : paperClip.uiColor === 'orange' ? '#fdba74' : '#fca5a5' }}>
            {paperClip.narratorText}
          </div>
        </Section>

        <Section title="Gaze / paper zone">
          <KV k="Zone" v={paperZoneStateRef.current?.zone ?? '-'} />
          <KV k="Region" v={paperZoneStateRef.current?.region ?? '-'} />
          <KV k="Off-screen" v={paperZoneStateRef.current?.offScreen ? 'yes' : 'no'} />
          {/* PaperRegionDetector (image processing) — actual paper-on-desk
              detection status. Independent of the fiducial-marker code path
              that drives the "Homography" line below. */}
          <KV k="Paper (img-proc)" v={paperDetectInfo.found
            ? `found (${(paperDetectInfo.conf * 100).toFixed(0)}%)`
            : `none (${(paperDetectInfo.conf * 100).toFixed(0)}%)`} />
          <KV k="Homography" v={(() => {
            const k = paperZoneStateRef.current?.homographyKind ?? 'none';
            const conf = paperZoneStateRef.current?.homographyConfidence ?? 0;
            const label =
              k === 'full'        ? '4-point DLT'
              : k === 'affine'      ? '3-point affine'
              : k === 'calibration' ? 'calibration (lost)'
              : 'none';
            return `${label} · ${(conf * 100).toFixed(0)}%`;
          })()} />
        </Section>

        {OBS_VEC_ENABLED && obsVec && (
          <Section title="Observation x(t) · ?obsVec (실험)">
            <KV k="Registry R(t)" v={`${obsVec.targetLikelihoods
              ? obsVec.targetLikelihoods.length - 1 : 0} targets`} />
            <KV k="Gaze top" v={`${obsVec.gazeTop?.type ?? '-'} · H=${
              obsVec.gazeEntropy?.toFixed(2) ?? '-'}b`} />
            <KV k="Paper zone" v={obsVec.paperZone ?? '-'} />
            <KV k="Homography" v={`${obsVec.homographyKind} · ${
              (obsVec.homographyConfidence * 100).toFixed(0)}%`} />
            <KV k="Objects" v={`${obsVec.numObjects} active · ${obsVec.numCoasting} coasting`} />
            <KV k="phone/pen/book" v={`${obsVec.phoneVisible ? 'Y' : '–'} / ${
              obsVec.penVisible ? 'Y' : '–'} / ${obsVec.bookVisible ? 'Y' : '–'}`} />
            <KV k="Hand·object" v={obsVec.handObjectInteraction} />
            <KV k="examMode" v={EXAM_MODE} />
            {obsInterp && <KV k="Decode" v={`${obsInterp.action} · ${obsInterp.basis}`} />}
            {obsInterp && <KV k="Label" v={obsInterp.neutralLabel} />}
          </Section>
        )}

        <Section title="3-zone dwell">
          <ZoneBar label="inside"   pct={pct(dw.inside)}   color="#22c55e" />
          <ZoneBar label="boundary" pct={pct(dw.boundary)} color="#eab308" />
          <ZoneBar label="outside"  pct={pct(dw.outside)}  color="#ef4444" />
          <KV k="Total" v={`${(dw.total / 1000).toFixed(1)}s`} />
          <button onClick={resetDwell} style={btnStyle}>Reset dwell</button>
        </Section>

        <Section title="Boundary margin">
          <KV k="Current" v={margin.toFixed(2)} />
          <input type="range" min={0.05} max={0.35} step={0.01} value={margin}
            onChange={e => { setMargin(Number(e.target.value)); resetDwell(); }}
            style={{ width: '100%' }} />
        </Section>

        <Section title="5-pillar: hand state">
          <KV k="State" v={writingState} />
          <KV k="Hands" v={String(hand.handCount)} />
          <KV k="Pen grip" v={hand.penGrip.isGrip
            ? `yes (${hand.penGripSustainFrames}f, ${(hand.penGrip.confidence*100).toFixed(0)}%)`
            : 'no'} />
          <KV k="Pen object" v={penObject.present
            ? `present (${(penObject.confidence*100).toFixed(0)}%)`
            : `${(penObject.confidence*100).toFixed(0)}%`} />
          <KV k="Phone grip" v={hand.phoneGrip.isGrip
            ? `yes (${hand.gripSustainFrames}f, ${(hand.phoneGrip.confidence*100).toFixed(0)}%)`
            : 'no'} />
          <KV k="Phone object" v={phoneObject.present
            ? `present (${(phoneObject.confidence*100).toFixed(0)}%)`
            : `${(phoneObject.confidence*100).toFixed(0)}%`} />
          <KV k="ML model" v={mlStatus.status === 'ready' ? '✓ ready'
            : mlStatus.status === 'loading' ? '⏳ loading…'
            : `✗ FAILED (${(mlStatus.error || 'unknown').substring(0, 40)})`} />
          <KV k="ML class" v={handObj.confidence > 0
            ? `${handObj.dominant} (${(handObj.confidence*100).toFixed(0)}%)`
            : mlStatus.status === 'ready' ? 'idle (no hand)' : '—'} />
          {handObj.confidence > 0 && (
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2, lineHeight: 1.4 }}>
              <div>none&nbsp;<ProbBar pct={handObj.noneProb*100} color="#6b7280" /></div>
              <div>pen&nbsp;&nbsp;<ProbBar pct={handObj.penProb*100} color="#22c55e" /></div>
              <div>phone <ProbBar pct={handObj.phoneProb*100} color="#ef4444" /></div>
            </div>
          )}
          <KV k="isWriting" v={hand.isWriting ? 'yes' : 'no'} />
        </Section>

        <Section title="5-pillar: bimanual coordination">
          {hand.handCount >= 2 && hand.secondary ? (
            <>
              <KV k="Kind" v={coordLabel} />
              <KV k="Sustain" v={`${coordSustain} frames`} />
              <KV k="2nd pen" v={hand.secondary.penGrip.isGrip
                ? `yes (${hand.secondary.penSustainFrames}f, ${(hand.secondary.penGrip.confidence*100).toFixed(0)}%)`
                : 'no'} />
              <KV k="2nd phone" v={hand.secondary.phoneGrip.isGrip
                ? `yes (${hand.secondary.phoneSustainFrames}f, ${(hand.secondary.phoneGrip.confidence*100).toFixed(0)}%)`
                : 'no'} />
              {coordKind && coordSustain >= 3 && (
                <div style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4, color: coordColor }}>
                  {coordLabel} sustained {coordSustain}f
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.55 }}>
              {hand.handCount < 2 ? `${hand.handCount} hand(s) detected — bimanual inactive` : 'secondary info unavailable'}
            </div>
          )}
        </Section>

        <Section title="Arm x object cross-check">
          {armFallback.active && armObjects.length > 0 ? (
            armObjects.slice(0, 3).map((m, i) => (
              <div key={i} style={{ marginBottom: 6 }}>
                <KV k={m.object.labelKo} v={`${(m.combinedConfidence * 100).toFixed(0)}%`} />
                <div style={{ fontSize: 11, opacity: 0.7, lineHeight: 1.4,
                  color: m.combinedConfidence >= 0.6 ? '#fca5a5'
                    : m.combinedConfidence >= 0.35 ? '#fdba74' : '#fde047' }}>
                  {m.narratorText}
                </div>
                <div style={{ fontSize: 10, opacity: 0.5 }}>
                  IoU {m.iou.toFixed(2)} / dist {m.centerDistance.toFixed(2)}
                </div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: 12, opacity: 0.55 }}>
              {!armFallback.active ? 'hand detected — arm fallback inactive'
                : 'arm active, no overlapping objects'}
            </div>
          )}
        </Section>

        <Section title="Arm fallback (hand-lost backup)">
          {armFallback.active && armFallback.primary ? (
            <>
              <KV k="Side" v={armFallback.primary.side} />
              <KV k="Posture" v={armFallback.primary.posture} />
              <KV k="Elbow angle" v={`${armFallback.primary.elbowAngleDeg.toFixed(0)}deg`} />
              <KV k="Arms visible" v={String(armFallback.arms.length)} />
              <ZoneBar label="confidence" pct={armFallback.primary.confidence * 100}
                color={armFallback.uiColor === 'yellow' ? '#eab308'
                  : armFallback.uiColor === 'orange' ? '#f97316' : '#ef4444'} />
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4, lineHeight: 1.4,
                color: armFallback.uiColor === 'yellow' ? '#fde047'
                  : armFallback.uiColor === 'orange' ? '#fdba74' : '#fca5a5' }}>
                {armFallback.narratorText}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.55 }}>
              {hand.detected ? 'hand directly detected (fallback skip)'
                : armFallback.narratorText || 'no pose data'}
            </div>
          )}
        </Section>

        <Section title="Hand tremor (1s window)">
          <KV k="Amplitude" v={`${hand.tremor.amplitudePx.toFixed(2)} px`} />
          <KV k="Freq" v={`${hand.tremor.freqHz.toFixed(1)} Hz`} />
          <KV k="Samples" v={String(hand.tremor.count)} />
          <ZoneBar label="confidence" pct={hand.tremor.confidence * 100} color="#06b6d4" />
          <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4, lineHeight: 1.4 }}>
            {(() => {
              const f = hand.tremor.freqHz;
              const a = hand.tremor.amplitudePx;
              if (hand.tremor.count < 5) return 'accumulating samples';
              if (a < 1) return 'no signal';
              if (a > 25) return `gross motion (a=${a.toFixed(0)}px > 25 → not tremor)`;
              if (f < 3 || f > 8) return `out of band (3–8 Hz)`;
              return `in band ${f.toFixed(1)}Hz, a=${a.toFixed(1)}px`;
            })()}
          </div>
        </Section>

        <Section title="Head tremor (1s window)">
          <KV k="Amplitude" v={`${headTremor.amplitudePx.toFixed(2)} px`} />
          <KV k="Freq" v={`${headTremor.freqHz.toFixed(1)} Hz`} />
          <KV k="Samples" v={String(headTremor.count)} />
          <ZoneBar label="confidence" pct={headTremor.confidence * 100} color="#a855f7" />
          <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4, lineHeight: 1.4 }}>
            {(() => {
              const f = headTremor.freqHz;
              const a = headTremor.amplitudePx;
              if (headTremor.count < 10) return 'accumulating samples';
              if (a < 0.5) return 'no signal';
              if (a > 20) return `head motion (a=${a.toFixed(0)}px > 20 → looking around)`;
              if (f < 2 || f > 6) return `out of band (2–6 Hz)`;
              return `in band ${f.toFixed(1)}Hz, a=${a.toFixed(1)}px`;
            })()}
          </div>
        </Section>

        <Section title="하향 자세 (1.5s sustain, 60s window)">
          <KV k="State" v={downPosture.active
            ? `ACTIVE (${downPosture.sustainedSec.toFixed(1)}s)`
            : downPosture.sustainedSec > 0
              ? `building (${downPosture.sustainedSec.toFixed(1)}s)`
              : 'idle'} />
          <KV k="반복 빈도" v={downPosture.escalation === 'frequent'
            ? `반복 누적 (${downPosture.eventCountWindow}회/60s)`
            : downPosture.escalation === 'occasional'
              ? `반복 (${downPosture.eventCountWindow}회/60s)`
              : '없음'} />
          <ZoneBar label="신호 종합 점수" pct={downPosture.score * 100}
            color={downPosture.escalation === 'frequent' ? '#ef4444'
              : downPosture.active ? '#ef4444'
              : downPosture.score > 0.4 ? '#eab308' : '#22c55e'} />
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4, lineHeight: 1.5 }}>
            pitch&nbsp;{(downPosture.signals.pitch*100).toFixed(0)}%
            &nbsp;twist&nbsp;{(downPosture.signals.twist*100).toFixed(0)}%
            &nbsp;shTilt&nbsp;{(downPosture.signals.shTilt*100).toFixed(0)}%
            &nbsp;wrist&nbsp;{(downPosture.signals.wrist*100).toFixed(0)}%
            &nbsp;gaze&nbsp;{(downPosture.signals.gaze*100).toFixed(0)}%
          </div>
          {/* Plain-Korean narrator — translates the dominant signals into
              a short sentence the demo audience can follow. */}
          {(downPosture.active || downPosture.score > 0.3) && (() => {
            const parts: string[] = [];
            if (downPosture.signals.pitch > 0.5)  parts.push('머리를 크게 숙임');
            if (downPosture.signals.twist > 0.5)  parts.push('상체를 비틂');
            if (downPosture.signals.shTilt > 0.5) parts.push('어깨가 기움');
            if (downPosture.signals.wrist > 0.5)  parts.push('손이 화면 아래로 사라짐');
            if (downPosture.signals.gaze > 0.5)   parts.push('시선이 시험지 아래로 향함');
            const verdict = downPosture.escalation === 'frequent'
              ? `— 반복 패턴 (${downPosture.eventCountWindow}회)` : downPosture.active ? '— 지속 관찰됨' : '— 관찰 중';
            return (
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 6, lineHeight: 1.5,
                color: downPosture.escalation === 'frequent' ? '#fca5a5'
                  : downPosture.active ? '#fde047' : '#9ca3af' }}>
                {parts.length > 0 ? parts.join(' · ') + ' ' + verdict
                  : '뚜렷한 신호 없음 ' + verdict}
              </div>
            );
          })()}
          {downPostureEvents.length > 0 && (
            <div style={{ fontSize: 11, marginTop: 8, padding: '6px 8px',
              background: '#0b0d10', borderRadius: 4, border: '1px solid #1f2630' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: 4 }}>
                <span style={{ opacity: 0.6 }}>Recent downPosture events:</span>
                <span>
                  <button onClick={() => {
                    const blob = new Blob([JSON.stringify({
                      sessionStartIso: new Date(sessionStartRef.current).toISOString(),
                      exportedAtIso: new Date().toISOString(),
                      events: downPostureEvents.map(e => ({
                        atIso: new Date(e.ts).toISOString(),
                        durationSec: e.dur,
                      })),
                    }, null, 2)], { type: 'application/json' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `down-posture-events-${Date.now()}.json`;
                    a.click();
                  }} style={{ fontSize: 10, padding: '2px 6px', marginRight: 4,
                    background: '#1f2630', border: '1px solid #2d3743',
                    color: '#9ca3af', borderRadius: 3, cursor: 'pointer' }}>
                    JSON 내보내기
                  </button>
                  <button onClick={() => setDownPostureEvents([])}
                    style={{ fontSize: 10, padding: '2px 6px',
                      background: '#1f2630', border: '1px solid #2d3743',
                      color: '#9ca3af', borderRadius: 3, cursor: 'pointer' }}>
                    리셋
                  </button>
                </span>
              </div>
              {downPostureEvents.slice().reverse().map((e, i) => {
                const d = new Date(e.ts);
                const hh = String(d.getHours()).padStart(2, '0');
                const mm = String(d.getMinutes()).padStart(2, '0');
                const ss = String(d.getSeconds()).padStart(2, '0');
                return (
                  <div key={e.ts} style={{ color: i === 0 ? '#fca5a5' : '#9ca3af',
                    fontVariantNumeric: 'tabular-nums' }}>
                    {hh}:{mm}:{ss} · {e.dur.toFixed(1)}s
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <Section title="3-pillar: detected objects">
          {obj.detections.length === 0
            ? <div style={{ fontSize: 12, opacity: 0.55 }}>no objects</div>
            : obj.detections.map((d, i) => <ObjectRow key={`${d.label}_${i}`} det={d} />)}
          <div style={{ fontSize: 10, opacity: 0.5, marginTop: 6, lineHeight: 1.4 }}>
            Policy: records presence regardless of use intent.
          </div>
        </Section>

        <Section title="Detection summary">
          <KV k="Objects" v={String(obj.detections.length)} />
          <KV k="Pose valid" v={pose.head.valid ? 'yes' : 'no'} />
          <KV k="Face" v={face.hasFace ? '1' : '0'} />
          <KV k="Paper clip" v={paperClip.severity} />
          <KV k="Paper visible" v={`${(paperClip.visibleFraction * 100).toFixed(0)}%`} />
          <KV k="Multi-paper" v={multiPaper.secondaryCount > 0
            ? `${multiPaper.secondaryCount}매 감지 (${(multiPaper.confidence * 100).toFixed(0)}%)`
            : '없음'} />
        </Section>

        <Section title="3꼭지: multi-paper detection">
          <KV k="Secondary papers" v={String(multiPaper.secondaryCount)} />
          {/* Phase 3-C — Primary selection by gaze hits */}
          <KV k="Primary (by gaze)" v={primarySel.primaryId
            ? `${primarySel.primaryId} · conf ${(primarySel.confidence*100).toFixed(0)}%`
            : '—'} />
          {Object.keys(primarySel.hitsById).length > 0 && (
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2, lineHeight: 1.4 }}>
              Hits 5s window: {Object.entries(primarySel.hitsById)
                .map(([id, n]) => `${id}=${n}`).join(' · ')}
            </div>
          )}
          <ZoneBar label="confidence" pct={multiPaper.confidence * 100}
            color={multiPaper.uiColor === 'green' ? '#22c55e'
              : multiPaper.uiColor === 'yellow' ? '#eab308'
              : multiPaper.uiColor === 'orange' ? '#f97316' : '#ef4444'} />
          <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4, lineHeight: 1.4,
            color: multiPaper.uiColor === 'green' ? '#86efac'
              : multiPaper.uiColor === 'yellow' ? '#fde047'
              : multiPaper.uiColor === 'orange' ? '#fdba74' : '#fca5a5' }}>
            {multiPaper.narratorText}
          </div>
          {multiPaper.secondaryCenters.length > 0 && multiPaper.secondaryCenters.map((c, i) => (
            <KV key={i} k={`#${i + 1} center`}
              v={`(${(c.x * 100).toFixed(0)}%, ${(c.y * 100).toFixed(0)}%)`} />
          ))}
          <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4, lineHeight: 1.4 }}>
            Scans 8×5 grid outside primary paper · 2 Hz · requires ≥2 marker hits/cluster
          </div>
        </Section>

        <Section title="4-pillar: joint attention">
          <KV k="Multi-face" v={`${jaFrame.faceCount} (hook:${multiFace.faces.length})`} />
          {jaFrame.faceCount < 2 ? (
            <div style={{ fontSize: 11, opacity: 0.55, lineHeight: 1.4 }}>
              2+ faces needed. Pair-assessment / collaborative scenario only.
            </div>
          ) : (
            <>
              <KV k="Mutual gaze" v={(() => {
                const m = jaFrame.mutualGazes[0];
                if (!m) return 'none';
                const sc = m.softScore;
                const a = m.angleADeg;
                const b = m.angleBDeg;
                const status = m.sustained ? 'ACTIVE' : 'building';
                const detail = sc != null ? ` score=${(sc*100).toFixed(0)}%` : '';
                const angs = (a != null && b != null) ? ` ∠ ${a.toFixed(0)}°/${b.toFixed(0)}°` : '';
                return status + detail + angs;
              })()} />
              <KV k="Joint attn (paper)" v={
                jaFrame.jointAttentions.length > 0
                  ? `${jaFrame.jointAttentions[0].participantIds.length} looking`
                  : 'none'
              } />
              {jaFrame.assignments.map(a => (
                <KV key={a.faceId} k={a.faceId}
                  v={a.target.kind === 'partner_face' ? `eye:${a.target.partnerId}`
                    : a.target.kind === 'common_roi' ? `roi:${a.target.roiId}`
                    : 'elsewhere'} />
              ))}

              {/* Phase 2-E: Custom ROI controls */}
              <div style={{ marginTop: 8, padding: '6px 8px', background: '#0b0d10',
                borderRadius: 4, border: '1px solid #1f2630' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, opacity: 0.75 }}>
                    Custom ROIs ({customRois.length})
                  </span>
                  <button onClick={() => setRoiDrawMode(v => !v)}
                    style={{ fontSize: 10, padding: '2px 8px',
                      background: roiDrawMode ? '#ef4444' : '#1f2630',
                      border: '1px solid #2d3743',
                      color: roiDrawMode ? '#fff' : '#9ca3af',
                      borderRadius: 3, cursor: 'pointer' }}>
                    {roiDrawMode ? '✕ 그리기 모드 종료' : '▢ ROI 그리기'}
                  </button>
                </div>
                {customRois.length === 0 ? (
                  <div style={{ fontSize: 10, opacity: 0.5 }}>
                    {roiDrawMode ? '비디오에서 클릭+드래그로 사각형 그리기' : '없음 (기본 paper ROI만 사용 중)'}
                  </div>
                ) : (
                  customRois.map(r => (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', fontSize: 10, marginTop: 2 }}>
                      <span style={{ color: '#fde047' }}>● {r.id}</span>
                      <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>
                        {(r.bbox.w*100).toFixed(0)}×{(r.bbox.h*100).toFixed(0)}%
                      </span>
                      <button onClick={() => setCustomRois(rs => rs.filter(x => x.id !== r.id))}
                        style={{ fontSize: 9, padding: '0 4px', background: '#1f2630',
                          border: '1px solid #2d3743', color: '#fca5a5',
                          borderRadius: 2, cursor: 'pointer' }}>삭제</button>
                    </div>
                  ))
                )}
              </div>

              {/* Phase 2-C: Gantt timeline of dyadic state — last 60s */}
              <div style={{ marginTop: 8, fontSize: 10, opacity: 0.65, marginBottom: 2 }}>
                Dyadic timeline (last 60s):
              </div>
              <JaGanttTimeline samplesRef={jaTimelineRef} />

              {/* Phase 2-A: cumulative dyadic metrics (research) */}
              <div style={{ marginTop: 8, padding: '6px 8px', background: '#0b0d10',
                borderRadius: 4, border: '1px solid #1f2630' }}>
                <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 4 }}>
                  Dyadic metrics (since session start):
                </div>
                <KV k="Observed" v={`${(jaFrame.metrics.dyadObservedMs/1000).toFixed(1)}s`} />
                <ZoneBar label="JAI" pct={jaFrame.metrics.jai * 100} color="#22c55e" />
                <ZoneBar label="MGI" pct={jaFrame.metrics.mgi * 100} color="#06b6d4" />
                <ZoneBar label="disengaged" pct={jaFrame.metrics.disengagementRate * 100} color="#6b7280" />
                <KV k="Asymmetry"
                  v={isNaN(jaFrame.metrics.initiationAsymmetry) ? '—'
                    : jaFrame.metrics.initiationAsymmetry.toFixed(2)} />
                <KV k="Sync lag (mean)"
                  v={isNaN(jaFrame.metrics.meanSyncLagMs)
                    ? `— (${jaFrame.metrics.syncLagSamples} samples)`
                    : `${jaFrame.metrics.meanSyncLagMs.toFixed(0)} ms (${jaFrame.metrics.syncLagSamples})`} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <button onClick={() => {
                    const now = performance.now();
                    const wallNow = Date.now();
                    const blob = new Blob([JSON.stringify({
                      exportedAtIso: new Date().toISOString(),
                      metrics: jaFrame.metrics,
                      faceCount: jaFrame.faceCount,
                      currentState: jaFrame.dyadState,
                      timeline: jaTimelineRef.current.map(s => ({
                        // Convert performance.now() to wall-clock ms
                        atMs: wallNow - (now - s.ts),
                        state: s.state,
                      })),
                    }, null, 2)], { type: 'application/json' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `joint-attention-${Date.now()}.json`;
                    a.click();
                  }} style={{ fontSize: 10, padding: '2px 6px',
                    background: '#1f2630', border: '1px solid #2d3743',
                    color: '#9ca3af', borderRadius: 3, cursor: 'pointer' }}>
                    JSON 내보내기
                  </button>
                </div>
                <div style={{ fontSize: 10, opacity: 0.55, marginTop: 6, lineHeight: 1.4 }}>
                  JAI = % time both gaze same ROI;
                  MGI = % time mutual eye-contact;
                  Asymmetry ∈ [-1,+1], 0 = balanced;
                  Sync lag = ms one face follows other to ROI.
                </div>
              </div>
            </>
          )}
        </Section>
      </aside>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  marginTop: 6, padding: '4px 8px', fontSize: 12,
  background: '#1f2630', color: '#e6edf3',
  border: '1px solid #2a3340', borderRadius: 4, cursor: 'pointer',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 16 }}>
      <div style={{ textTransform: 'uppercase', letterSpacing: 0.6,
        fontSize: 11, opacity: 0.55, marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'grid', rowGap: 4 }}>{children}</div>
    </section>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ opacity: 0.7 }}>{k}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>
    </div>
  );
}

function ObjectRow({ det }: { det: Detection }) {
  const color = det.salience === 'high' ? '#ef4444'
    : det.salience === 'low' ? '#eab308' : '#6b7280';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '8px 1fr 56px',
      alignItems: 'center', gap: 6, fontSize: 13 }}>
      <span style={{ display: 'inline-block', width: 8, height: 8,
        borderRadius: 4, background: color }} />
      <span style={{ opacity: 0.85 }}>
        {det.labelKo}
        {det.salience === 'high' && <span style={{ opacity: 0.6, marginLeft: 4 }}>present</span>}
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right', opacity: 0.75 }}>
        {(det.score * 100).toFixed(0)}%
      </span>
    </div>
  );
}

/** Phase 2-C — Gantt-style horizontal timeline of dyadic states over
 *  the last 60 s. Single row, color-coded blocks. Pulls from
 *  jaTimelineRef on every render of the parent and redraws a small
 *  canvas. */
function JaGanttTimeline({ samplesRef, windowMs = 60_000 }: {
  samplesRef: React.MutableRefObject<Array<{ ts: number; state: DyadState }>>;
  windowMs?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    let id = 0;
    const draw = () => {
      id = requestAnimationFrame(draw);
      const c = canvasRef.current;
      if (!c) return;
      const w = c.clientWidth | 0;
      const h = c.clientHeight | 0;
      if (c.width !== w * 2) c.width = w * 2;  // hi-DPI
      if (c.height !== h * 2) c.height = h * 2;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(2, 0, 0, 2, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#1f2630';
      ctx.fillRect(0, 0, w, h);
      const now = performance.now();
      const t0 = now - windowMs;
      const buf = samplesRef.current;
      const colorMap: Record<DyadState, string> = {
        absent:     '#374151',
        disengaged: '#6b7280',
        individual: '#eab308',
        joint:      '#22c55e',
        mutual:     '#06b6d4',
      };
      // Walk samples and emit blocks. Use ts → x mapping.
      for (let i = 0; i < buf.length; i++) {
        const s = buf[i];
        const next = i + 1 < buf.length ? buf[i + 1].ts : now;
        const x1 = ((s.ts - t0) / windowMs) * w;
        const x2 = ((next - t0) / windowMs) * w;
        const xs = Math.max(0, x1);
        const xe = Math.min(w, x2);
        if (xe <= xs) continue;
        ctx.fillStyle = colorMap[s.state];
        ctx.fillRect(xs, 2, xe - xs, h - 4);
      }
      // Axis ticks every 10 s
      ctx.strokeStyle = '#0b0d1080';
      ctx.lineWidth = 1;
      for (let dt = 0; dt <= windowMs; dt += 10_000) {
        const x = (1 - dt / windowMs) * w;
        ctx.beginPath();
        ctx.moveTo(x, 0); ctx.lineTo(x, h);
        ctx.stroke();
      }
    };
    id = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(id);
  }, [samplesRef, windowMs]);
  return (
    <div>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 24,
        borderRadius: 3 }} />
      <div style={{ display: 'flex', gap: 8, fontSize: 10, opacity: 0.75,
        marginTop: 4, flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10,
          background: '#06b6d4', borderRadius: 2, verticalAlign: 'middle',
          marginRight: 3 }} />mutual</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10,
          background: '#22c55e', borderRadius: 2, verticalAlign: 'middle',
          marginRight: 3 }} />joint</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10,
          background: '#eab308', borderRadius: 2, verticalAlign: 'middle',
          marginRight: 3 }} />individual</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10,
          background: '#6b7280', borderRadius: 2, verticalAlign: 'middle',
          marginRight: 3 }} />disengaged</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10,
          background: '#374151', borderRadius: 2, verticalAlign: 'middle',
          marginRight: 3 }} />absent</span>
        <span style={{ marginLeft: 'auto' }}>← 60s ago · now →</span>
      </div>
    </div>
  );
}

function ProbBar({ pct, color }: { pct: number; color: string }) {
  return (
    <span style={{ display: 'inline-block', width: 90, height: 6, verticalAlign: 'middle',
      background: '#1f2630', borderRadius: 3, overflow: 'hidden', marginLeft: 4 }}>
      <span style={{ display: 'block', width: `${Math.min(100, Math.max(0, pct))}%`,
        height: '100%', background: color }} />
    </span>
  );
}

function ZoneBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 56px', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 12, opacity: 0.8 }}>{label}</span>
      <div style={{ height: 8, background: '#1f2630', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color }} />
      </div>
      <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Phase 2-E — Custom ROI overlay: user-drawn rectangles for joint
// attention. Always renders existing ROIs (clickable to delete) and,
// when drawMode is on, captures mouse drag to define new ones.
//
// Coordinate handling: the underlying <video> has CSS `scaleX(-1)`, so
// a click at screen-X=200px in a 800px wide video really points at
// image-X=600. We compute image-normalized coords by un-mirroring X,
// then re-mirror the SVG so the rendered rectangle aligns visually.
// ──────────────────────────────────────────────────────────────────
interface CustomRoiOverlayProps {
  rois: Roi[];
  setRois: React.Dispatch<React.SetStateAction<Roi[]>>;
  drawMode: boolean;
  /** Reserved-ID set so we never collide with built-in ROIs. */
  paperRoi: Roi;
}

function CustomRoiOverlay({ rois, setRois, drawMode, paperRoi }: CustomRoiOverlayProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCur, setDragCur] = useState<{ x: number; y: number } | null>(null);

  // Convert screen client (mouse) coords → image-normalized coords,
  // accounting for the video's CSS scaleX(-1).
  const toImgCoords = (clientX: number, clientY: number) => {
    const el = svgRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const rawX = (clientX - rect.left) / rect.width;
    const rawY = (clientY - rect.top) / rect.height;
    // Un-mirror X because we mirror the SVG for rendering alignment.
    return {
      x: Math.max(0, Math.min(1, 1 - rawX)),
      y: Math.max(0, Math.min(1, rawY)),
    };
  };

  const onDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!drawMode) return;
    const p = toImgCoords(e.clientX, e.clientY);
    setDragStart(p);
    setDragCur(p);
  };
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!drawMode || !dragStart) return;
    setDragCur(toImgCoords(e.clientX, e.clientY));
  };
  const onUp = () => {
    if (!drawMode || !dragStart || !dragCur) { setDragStart(null); setDragCur(null); return; }
    const x = Math.min(dragStart.x, dragCur.x);
    const y = Math.min(dragStart.y, dragCur.y);
    const w = Math.abs(dragCur.x - dragStart.x);
    const h = Math.abs(dragCur.y - dragStart.y);
    setDragStart(null); setDragCur(null);
    // Skip too-tiny boxes (likely an accidental click).
    if (w < 0.02 || h < 0.02) return;
    // Generate a unique ID that avoids the paper ROI's reserved name.
    const used = new Set([paperRoi.id, ...rois.map(r => r.id)]);
    let i = 0;
    while (used.has(`roi_${i}`)) i++;
    setRois(prev => [...prev, { id: `roi_${i}`, bbox: { x, y, w, h } }]);
  };

  // Compute current drag rectangle in image coords (for preview).
  const preview = dragStart && dragCur ? {
    x: Math.min(dragStart.x, dragCur.x),
    y: Math.min(dragStart.y, dragCur.y),
    w: Math.abs(dragCur.x - dragStart.x),
    h: Math.abs(dragCur.y - dragStart.y),
  } : null;

  return (
    <svg ref={svgRef}
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        pointerEvents: drawMode ? 'auto' : 'none',
        cursor: drawMode ? 'crosshair' : 'default',
        transform: 'scaleX(-1)',  // mirror to match the video
      }}
      viewBox="0 0 1 1" preserveAspectRatio="none"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
    >
      {rois.map(r => (
        <g key={r.id}>
          <rect x={r.bbox.x} y={r.bbox.y} width={r.bbox.w} height={r.bbox.h}
            fill="#fde04722" stroke="#fde047" strokeWidth={0.003}
            style={{ pointerEvents: drawMode ? 'none' : 'auto', cursor: 'pointer' }} />
          <text x={r.bbox.x + 0.005} y={r.bbox.y + 0.025}
            fontSize={0.018} fill="#fde047"
            transform={`scale(-1, 1) translate(${-(r.bbox.x*2 + 0.01)}, 0)`}>
            {r.id}
          </text>
        </g>
      ))}
      {preview && (
        <rect x={preview.x} y={preview.y} width={preview.w} height={preview.h}
          fill="#22c55e22" stroke="#22c55e" strokeWidth={0.004} strokeDasharray="0.01 0.005" />
      )}
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────
// Draggable paper-corner handles — user can mouse-drag the 4 outline
// corners to manually calibrate the homography. Recomputes
// paperHomographyRef + calibHomographyRef on every change.
// ──────────────────────────────────────────────────────────────────
interface PaperCornerHandlesProps {
  paperCornersRef: React.MutableRefObject<Point2D[]>;
  paperHomographyRef: React.MutableRefObject<Float64Array | null>;
  calibCornersRef: React.MutableRefObject<Point2D[]>;
  calibHomographyRef: React.MutableRefObject<Float64Array | null>;
}

function PaperCornerHandles(props: PaperCornerHandlesProps): React.ReactElement {
  const { paperCornersRef, paperHomographyRef, calibCornersRef, calibHomographyRef } = props;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Local state mirrors paperCornersRef so the SVG re-renders on tick or
  // drag — the ref itself updates fly under React's radar.
  const [corners, setCorners] = useState<Point2D[]>([...paperCornersRef.current]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // Tick — pull latest ref every 250ms to track auto-detected corners.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (dragIdx !== null) return;  // don't override while user drags
      const c = paperCornersRef.current;
      // only update state if changed enough
      setCorners((prev) => {
        for (let i = 0; i < 4; i++) {
          if (Math.abs(prev[i].x - c[i].x) > 0.001 || Math.abs(prev[i].y - c[i].y) > 0.001) {
            return [...c];
          }
        }
        return prev;
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [paperCornersRef, dragIdx]);

  const handleMove = (clientX: number, clientY: number) => {
    if (dragIdx === null || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    // Video element has CSS scaleX(-1) mirror. The SVG below also has
    // transform: scaleX(-1) so handles appear visually aligned with the
    // mirrored video. Mouse coordinates are in screen space (un-mirrored),
    // so we un-mirror X before storing as raw image-normalized coord.
    const screenX = (clientX - rect.left) / rect.width;
    const imageX  = 1 - screenX;
    const y = (clientY - rect.top) / rect.height;
    const clamped = { x: Math.max(0, Math.min(1, imageX)), y: Math.max(0, Math.min(1, y)) };
    const next = corners.map((c, i) => (i === dragIdx ? clamped : c));
    setCorners(next);
    paperCornersRef.current = next;
    calibCornersRef.current = next;
    const H = computeHomography(next as [Point2D, Point2D, Point2D, Point2D], PAPER_DST);
    if (H) {
      paperHomographyRef.current = H;
      calibHomographyRef.current = new Float64Array(H);
    }
  };

  useEffect(() => {
    if (dragIdx === null) return;
    const onMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY);
    const onUp = () => setDragIdx(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragIdx, corners]);

  return (
    <div ref={wrapperRef}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
        overflow: 'visible', pointerEvents: 'none',
        // Match the video's CSS scaleX(-1) mirror so handles visually
        // align with the mirrored video + outline.
        transform: 'scaleX(-1)' }}>
        {corners.map((c, i) => (
          <circle key={i}
            cx={`${c.x * 100}%`} cy={`${c.y * 100}%`}
            r={dragIdx === i ? 12 : 9}
            fill={dragIdx === i ? '#fbbf24' : '#22c55e'}
            stroke="#fff" strokeWidth={2}
            style={{ cursor: 'grab', pointerEvents: 'auto' }}
            onMouseDown={(e) => { e.preventDefault(); setDragIdx(i); }}
          />
        ))}
      </svg>
    </div>
  );
}

export default LiveDemoApp;